import { Injectable, NgZone, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { v4 as uuid } from 'uuid';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { TimeService } from '@services/time-service';
import { RequestDedupeService } from '@services/request-dedupe-service';
import { fromActions } from '@store/actions';
import * as fromSelectors from '@store/selectors/app.selectors';
import { CacheService } from '@services/cache-service';
import { ConcernsWithCount } from 'client/app/report/report.component';
import { AiHairReport } from 'client/app/report/report.constants';
import { prescriptionVar } from '@shared/constants';
import {
  Table,
  ApiConnector,
  ParseKeys,
  apiObjectToJSON,
  RequestQueryPayload,
  Models,
  ApiClientConstant,
} from '@cureskin/api-client';
import {
  ServerApi,
  AiAssistantMessage,
  SendAiAssistantMessageRequest,
  DoctorProfile,
  Entity,
  OTPGenerateRequest,
  LoginGenerateOtpResponse,
  LoginRetryRequest,
  LoginRetryResponse,
  LoginVerifyRequest,
  LoginVerifyResponse,
  TruecallerV2VerifyRequestLoginMethodEnum,
  ExternalChannel,
  InitiateOrderPaymentResponse,
  PinCodes,
  CreateAddressBookRequest,
  CreateAddressBookResponse,
  UpdateAddressBookRequest,
  UpdateAddressBookResponse,
  FetchAddressBooksResponse,
  GetPinCodeInfoResponse,
  ReverseGeocodeResponse,
  ChangeDefaultResponse,
  DeleteAddressBookResponse,
} from '@cureskin/api-client-rn';
import { ServerAPIModels, ServerAPIResponse } from 'client/app/server-api.constants';
import type { AppWebBridgeService } from '@services/app-web-bridge-service';
import { AxiosResponse } from 'axios';
import { AppConfig } from '../../app/app.config';
import { WindowRefService } from '../window-ref-service';
import { LocalStorageService } from '../local-storage-service';
import { CookieService } from '../cookie-service';
import { BroadcastService } from '../broadcast-service';
import { DataStoreService } from '../data-store-service';
import { AllocatedRegimen, AuthConfig, FollowUpReportTreatmentUpdates, TranslateLastBotQuestionData } from '../../app/app-constant-types';
import { generateDigest } from '../utility';
import { resetCacheByName, withCache } from './cache-interceptor';
import {
  GetDeliveredMargIdsResponse,
  ShowNotificationPermissionPopupData,
  OnHoldUserResponseRequestBodyUserResponseEnum,
  ShowOnHoldPopUpResponseData,
  RegimenExpiryBannerResponse,
  GetCouponResponse,
  ProductUsageTrackingResponseData,
  GetSuitableFeedForUserResponse, GetRegimenFaqPostsForUserResponse, ObtainBranchLinkResponse,
  GetUserInstructionDetails,
  FindDeliveredProductsOfUserResponse,
  CheckAddressRequest,
  CheckAddressResponse,
  InitiateLoginRequest,
  InitiateLoginResponse,
  VerifyTokenResponse,
  VerifyTokenRequest,
  GetAddressesRequest,
  GetAddressesResponse,
  ImportAddressRequest,
  ImportAddressResponse,
  GetAuthorisedTokenRequest,
  GetAuthorisedTokenResponse,
  ShiprocketGenerateRequest,
  LoginGenerateShiprocketResponse,
  ShiprocketVerifyRequest,
  SubmitDefaultAnswersResponse,
  ReorderKitBannerResponse,
  PersonalisedPromotion, QuizNextQuestionResponse, QuizSubmitAnswerResponse,
  GetInstantCheckupsResponse,
  GetInstantCheckupsSortOrderEnum,
  GetMainUserPhotosRegimenClassEnum,
  InstantCheckup,
  InstantCheckupTypes,
  TestimonialResponseItem,
  FollowUpReportFeedbackQuestion,
  FollowUpReportFeedbackMessageMap,
  RegimenClass,
  GetQuestionResponsesResponse,
  SetQuestionResponseResponse,
  ProjectedQuestionData,
  GetHairReportResponse,
  AiResponse,
  InvestigationStatusResponse,
  ImprovementTimelineAndKeyTechnologiesData,
  ImprovementTimelineEntry,
  KeyTechnologiesEntry,
} from '@cureskin/api-client/src/server-api/api';
import { ValueOf } from '@cureskin/api-client/src/common';

export interface RegimenTimelineImages {
  timelineImage: string | null;
  keyTechnologiesImage: string | null;
}

/** Google Maps reverse-geocode response shape (the `results` field of `ReverseGeocodeResponse`). */
interface ReverseGeocodeData {
  results?: Array<{
    types?: Array<string>;
    address_components?: Array<{ long_name?: string; short_name?: string; types?: Array<string> }>;
    formatted_address?: string;
    geometry?: { location?: { lat?: number; lng?: number } };
  }>;
  status?: string;
  error_message?: string;
}

@Injectable()
export class ConnectionService {
  private actingUser: any;
  uniqueId: string;
  serverApi: ServerApi;
  eventServerApi: ServerApi;
  isVerified: boolean = false;
  regimenRefreshInterval: number = 2 * 60 * 1000;
  allowRegimenForceFetch: boolean = true;
  /** Latest in-flight regimen mutation. Regimen fetches await this so they can't race a pending server-side change. */
  private pendingRegimenMutation: Promise<unknown> = Promise.resolve();
  /** In-flight improvement-timeline fetch, shared across concurrent callers (e.g. the timeline + key-technologies banners on the regimen page) so the endpoint is hit once per user. Cleared on error so a retry re-fetches. */
  private improvementTimelineFetch: { userId: string; promise: Promise<ImprovementTimelineAndKeyTechnologiesData | null> } | null = null;
  prescriptionVar: any = prescriptionVar;
  isVariantAlreadySelected: boolean = false;
  tagCacheTableMapping: Record<string, string> = AppConfig.TagCacheTableMapping;
  user: any;
  private appWebBridge: AppWebBridgeService;
  constructor(private appConfig: AppConfig,
    private cookieService: CookieService,
    private windowRef: WindowRefService,
    private localStorage: LocalStorageService,
    private router: Router,
    private httpClientService: HttpClient,
    private broadcast: BroadcastService,
    private zone: NgZone,
    private dataStore: DataStoreService,
    private timeService: TimeService,
    private localStorageService: LocalStorageService,
    private store: Store,
    private cacheService: CacheService,
    private requestDedupe: RequestDedupeService,
    private injector: Injector) {
    this.updateUniqueId();
  }

  // ************************************ API UTILITY FUNCTIONS ************************************** //

  /**
   * Initializes the parse sdk with the api server url.
   * 1. Looks for session token in cookies and tries to login with that if user is not logged in.
   * 2. Updates few data in cookies.
   * 3. Fetches the updated user data from api, routes to onboarding is api fails fetching user.
   */
  initialize(): any {
    ApiConnector.initialize('myAppId', `${this.getParseUrl()}/api/parse`, { disableSingleInstance: true });
    ApiConnector.updateInterceptor(true);
    this.serverApi = this.requestDedupe.wrap(new ServerApi(undefined, this.getParseUrl()));
    this.eventServerApi = new ServerApi(undefined, this.getEventParseUrl());
    void this.updateUserFromCookies();
    this.updateCookies();
    void this.updateLocalUser();
    this.resetLocalStorageData();
    this.user = this.getActingUser();
  }

  private getAppBridgeService(): AppWebBridgeService {
    if (!this.appWebBridge) {
      // Lazy import to break circular dependency with AppWebBridgeService
      const { AppWebBridgeService: AWBService } = require('@services/app-web-bridge-service');
      this.appWebBridge = this.injector.get(AWBService);
    }
    return this.appWebBridge;
  }

  resetLocalStorageData(): void {
    this.localStorage.setJsonValue('CureSkin/orderStages', []);
  }

  get isUserLoggedIn(): boolean {
    return !!(this.getCurrentUser() && this.getSessionToken());
  }

  get isTestEnvironment(): boolean {
    const { hostname }: { hostname: string } = this.windowRef.nativeWindow.location;
    return hostname.startsWith('test')
      || hostname.startsWith('localhost')
      || hostname.includes('rc-app')
      || hostname.includes('dev-app')
      || this.isDevelopmentEnvironment;
  }

  get isDevelopmentEnvironment(): boolean {
    return this.appConfig.Shared.Server.env === 'development';
  }

  getParseUrl(): string {
    let url;
    switch (this.windowRef.nativeWindow.location.hostname) {
      case 'test-app.cureskin.com': {
        url = 'https://test-api.cureskin.com';
        break;
      }
      case 'app.cureskin.com': {
        url = 'https://api.cureskin.com';
        break;
      }
      case 'staging-app.cureskin.com': {
        url = 'https://staging-api.cureskin.com';
        break;
      }
      case 'localhost': {
        url = 'https://test-api.cureskin.com';
        break;
      }
      case 'canary-app.cureskin.com': {
        url = 'https://canary-api.cureskin.com';
        break;
      }
      case 'rc-app.cureskin.com': {
        url = 'https://rc-api.cureskin.com';
        break;
      }
      default: {
        if (this.windowRef.nativeWindow.location.hostname.includes('.rc-app.cureskin.com')) {
          url = `https://${this.windowRef.nativeWindow.location.hostname.replace('.rc-app.cureskin.com', '.rc-api.cureskin.com')}`;
          break;
        }
        if (this.windowRef.nativeWindow.location.hostname.includes('.dev-app.cureskin.com')) {
          url = `https://${this.windowRef.nativeWindow.location.hostname.replace('.dev-app.cureskin.com', '.dev-api.cureskin.com')}`;
          break;
        }
        url = `http://${this.windowRef.nativeWindow.location.hostname}:9000`;
      }
    }
    return url;
  }

  getEventParseUrl(): string {
    switch (this.windowRef.nativeWindow.location.hostname) {
      case 'app.cureskin.com': {
        return 'https://webhook-api.cureskin.com';
      }
      case 'canary-app.cureskin.com': {
        return  'https://webhook-api.cureskin.com';
      }
    }
    return this.getParseUrl();
  }

  getBaseUrl(): string {
    let url = `${this.windowRef.nativeWindow.location.protocol}//${this.windowRef.nativeWindow.location.hostname}`;
    if (this.windowRef.nativeWindow.location.port) url = `${url}:${this.windowRef.nativeWindow.location.port}`;
    return url;
  }

  getAiUrl(): string {
    return 'https://analyze.cureskin.com/uploadImageInstantCheckup';
  }

  userWithoutPrivateMainConcern(): boolean {
    const user = this.getActingUser();
    return this.isUserLoggedIn
    && !this.isInternalUser()
    && !user.get('PrivateMainConcern')
    && user.get('mainConcernMandatory')
    && !user.get('previousRegimenConcern');
  }

  get analyticsUrl(): string {
    let url;
    switch (this.windowRef.nativeWindow.location.hostname) {
      case 'test-app.cureskin.com': {
        url = 'https://test-analytics.cureskin.com';
        break;
      }
      case 'app.cureskin.com': {
        url = 'https://analytics.cureskin.com';
        break;
      }
      case 'staging-app.cureskin.com': {
        url = 'https://analytics.cureskin.com';
        break;
      }
      case 'localhost': {
        url = `http://${this.windowRef.nativeWindow.location.hostname}:9003`;
        break;
      }
      case 'canary-app.cureskin.com': {
        url = 'https://analytics.cureskin.com';
        break;
      }
      case 'rc-app.cureskin.com': {
        url = 'https://test-analytics.cureskin.com';
        break;
      }
      default: {
        url = 'https://analytics.cureskin.com';
      }
    }
    return url;
  }

  login(username: string, password: string): Promise<Table.User> {
    return ApiConnector.userLogIn<Table.User>(username, password);
  }

  getCurrentUser(): any {
    const user = ApiConnector.getCurrentUser() as Table.User & {isPaid?: boolean};
    if (user && !user.isPaid) {
      user.isPaid = this.isPaid.call(user, this);
    }
    return user;
  }

  getSessionToken(): string | undefined {
    return this.getCurrentUser()?.get('sessionToken');
  }

  get getUserLanguage(): string {
    return this.getCurrentUser()?.get('languagePreference') || this.appConfig.Shared.Languages.EN;
  }

  getValueOfLanguageString(languageString: any): string {
    if (languageString.id) return languageString.get(this.getUserLanguage) || languageString.get(this.appConfig.Shared.Languages.EN);
    return languageString[this.getUserLanguage] || languageString[this.appConfig.Shared.Languages.EN];
  }

  /**
   * Parse the relative url and queryparams out of full url string.
   * @param {string} path
   * @returns {[string , object]}
   */
  findRelativePathAndQueryString(path: string): [string, object] {
    const [relativePath, queryString]: Array<string> = (path || '').split('?');
    const queryParams = {};
    if (queryString) {
      const keyValues = queryString.split('&');
      keyValues.forEach((keyValue: string): void => {
        const [key, value]: Array<string> = keyValue.split('=');
        queryParams[key] = decodeURIComponent(value || '');
      });
    }
    return [relativePath, queryParams];
  }

  async redirectToLastKnowLocation(defaultUrl: string = '/', replaceUrl: boolean = undefined): Promise<void> {
    const redirectUrl: string = this.localStorage.getValue('CureSkin/redirectUrl') || defaultUrl;
    this.localStorage.delete('CureSkin/redirectUrl');
    await this.navigateToURL(redirectUrl, undefined, replaceUrl);
  }

  /**
   * Checks for user preferred language and if current website is not in that language code,
   * then it route the user to specific website with same route url.
   * @param {string} url
   * @returns {Promise<void>}
   */
  checkUserLanguageAndRedirect(url?: string): void {
    const user = this.getActingUser();
    if (!user || this.isInternalUser()) return;
    const storedLanguage = this.localStorage.getValue('CureSkin/language');
    if (storedLanguage !== user.get('languagePreference')) {
      this.localStorage.setValue('CureSkin/language', user.get('languagePreference'));
    }
    let urlPrefix = `${this.getBaseUrl()}`;
    const redirectTo = this.findRoutePath();
    if (!this.isDevelopmentEnvironment
      && user.get('languagePreference')
      && user.get('languagePreference') !== this.appConfig.Shared.Languages.EN
      && !this.windowRef.nativeWindow.location.pathname.startsWith(`/${user.get('languagePreference')}`)) {
      urlPrefix += `/${user.get('languagePreference')}`;
      if (url) this.windowRef.nativeWindow.location.href = `${urlPrefix}${url}`;
      else if (!url && redirectTo) this.windowRef.nativeWindow.location.href = `${urlPrefix}/${redirectTo}`;
      else this.windowRef.nativeWindow.location.href = `${urlPrefix}/`;
      return;
    }
    if (this.currentWebSiteLanguage === user.get('languagePreference') && !url) return;
    if (url) void this.navigateToURL(url);
    else if (!url && redirectTo) void this.navigateToURL(`/${redirectTo}`);
  }

  findRoutePath(): any {
    let routePath = this.windowRef.nativeWindow.location.pathname.substr(1) + this.windowRef.nativeWindow.location.search;
    const languages = Object.keys(this.appConfig.Shared.Languages).map((each: any): string => `/${each.toLowerCase()}/`);
    languages.forEach((each: string): void => {
      if (routePath.startsWith(each)) {
        const split = routePath.split(each);
        split.splice(0, 1);
        routePath = split.join('');
      }
    });
    return routePath;
  }

  /**
   * Helper function to find whether user is paid user or not. This can be accesses in 2 ways.
   * 1. this.conn.isPaid(userParseObject);
   * 2. this.userParseObject.isPaid();
   * This function to binded to user parse object in all places where all user object is fetched.
   * For example, getCurrentUser(), findUserByObjectId(), findUserByUserName().
   * @param argument
   * @returns {any}
   */
  isPaid(argument: any): any {
    let scope;
    let user;
    if (argument.className === '_User') {
      scope = this;
      user = argument;
    } else {
      scope = argument;
      user = this;
    }
    const exec = ((): boolean => ![
      scope.appConfig.Shared.User.OrderState.UNPAID,
      scope.appConfig.Shared.User.OrderState.NEW_USER,
    ].includes(user.get('orderState')));
    if (argument.className === '_User') return exec(); // returns boolean. eg: this.conn.isPaid(anyUser)
    return exec; // returns function when its binded. eg: userParseObject.isPaid()
  }

  get s4(): string {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  }

  updateUniqueId(): void {
    this.uniqueId = this.localStorage.getValue('user/es/id');
    if (this.uniqueId) return;
    this.uniqueId = this.s4 + this.s4 + this.s4 + this.s4 + this.s4;
    this.localStorage.setValue('user/es/id', this.uniqueId);
  }

  addScript(scriptUrl: string, onLoadCallback?: Function): void {
    const scriptTags = this.windowRef.nativeWindow.document.getElementsByTagName('script');
    let scriptAdded: boolean = false;
    for (let i = 0; i < scriptTags.length; i += 1) {
      if (scriptUrl === scriptTags[i].src) {
        scriptAdded = true;
      }
    }
    if (scriptAdded) return;
    const script = this.windowRef.nativeWindow.document.createElement('script');
    script.src = scriptUrl;
    script.defer = true;
    script.type = 'text/javascript';
    if (onLoadCallback instanceof Function) script.onload = onLoadCallback;
    this.windowRef.nativeWindow.document.head.appendChild(script);
  }

  addStyle(styleUrl: string, rel: string = 'stylesheet'): void {
    const linkTags = this.windowRef.nativeWindow.document.getElementsByTagName('link');
    let linkAdded: boolean = false;
    for (let i = 0; i < linkTags.length; i += 1) {
      if (styleUrl === linkTags[i].href) {
        linkAdded = true;
      }
    }
    if (linkAdded) return;
    const link = this.windowRef.nativeWindow.document.createElement('link');
    link.rel = rel;
    if (rel === 'stylesheet') {
      link.type = 'text/css';
    }
    link.href = styleUrl;
    this.windowRef.nativeWindow.document.head.appendChild(link);
  }

  removeStyle(styleUrl: string): void {
    const linkTags = this.windowRef.nativeWindow.document.getElementsByTagName('link');
    for (let i = 0; i < linkTags.length; i += 1) {
      if (styleUrl === linkTags[i].href) {
        const mynode = linkTags[i];
        mynode.parentNode.removeChild(mynode);
      }
    }
  }

  removeScript(url: string): void {
    const linkTags = this.windowRef.nativeWindow.document.getElementsByTagName('script');
    for (let i = 0; i < linkTags.length; i += 1) {
      if (linkTags[i].src.includes(url)) {
        const mynode = linkTags[i];
        mynode.parentNode.removeChild(mynode);
      }
    }
  }

  getSessionLessId(ignoreUserLogin: boolean = false): string {
    if (this.isUserLoggedIn && !ignoreUserLogin) return this.getActingUser().get('username');
    const interApplicationTrackingId = this.localStorage.getValue('user/capture/id');
    if (interApplicationTrackingId) {
      return interApplicationTrackingId;
    }
    return this.cookieService.get('deviceId') || this.uniqueId;
  }

  /**
   * Helper function to navigate to a specific route with a full route string.
   * It parses the route path, queryParams out from url and sends to router.
   * @param {string} url - route string.
   * @param {boolean} newTab - if true, it will open the external site url in iframe.
   * @param {boolean} replaceUrl - over writes the current url in the history state.
   * @returns {Promise<any>}
   */
  async navigateToURL(url: string, newTab: boolean = false, replaceUrl: boolean = undefined): Promise<any> {
    if (newTab) {
      await this.zone.run((): Promise<boolean> => this.router.navigate(['/iframe'], { queryParams: { src: url } }));
      return;
    }
    if (['/',
      this.windowRef.nativeWindow.location.origin,
      'https://test-app.cureskin.com',
      'https://app.cureskin.com',
      'https://staging-app.cureskin.com']
      .every((x: string): boolean => !url.startsWith(x))) {
      await this.zone.run((): Promise<boolean> => this.router.navigate(['/iframe'], { queryParams: { src: url } }));
      return;
    }
    const path = url.startsWith('http') ? `/${url.split('/').splice(3, 100).join('/')}` : url;
    const [relativePath, queryParams]: [string, object] = this.findRelativePathAndQueryString(path);
    /** If requested path is same as current page path,
     * then it routes to /empty module and back to requested path in order to trigger the route change for same url.
     */
    if (this.windowRef.nativeWindow.location.href.endsWith(path)) {
      await new Promise(async (resolve: any, reject: any): Promise<any> => {
        try {
          await this.zone.run(async (): Promise<boolean> => this.router.navigate(['/empty'], { skipLocationChange: true }));
          const relativePathArray = ['', '/'].includes(relativePath) ? ['/'] : relativePath.split('/');
          await this.zone.run(async (): Promise<boolean> => this.router.navigate(relativePathArray, { queryParams }));
          resolve();
        } catch (err) {
          reject(err);
        }
      });
      return;
    }
    const relativePathArray = ['', '/'].includes(relativePath) ? ['/'] : relativePath.split('/');
    await this.zone.run((): Promise<boolean> => this.router.navigate(relativePathArray, { queryParams, replaceUrl }));
  }

  // ************************************ API ************************************** //

  /**
   * Custom Parse api call interceptor.
   */
  async apiWall(requestPromise: () => Promise<any>): Promise<any> {
    try {
      return await requestPromise();
    } catch (error) {
      if (error.code === 209 || error.code === 206) {
        void this.logout();
        await this.router.navigate(['/onboarding/number']);
      } else if (error.code === 100 && error.message.includes('Unable to connect to the Parse API')) {
        this.broadcast.broadcast('CHECK_NETWORK_CONNECTION');
      }
      return Promise.reject(error);
    }
  }

  private cloudRun(apiName: string, payload?: any, options?: any): Promise<any> {
    return this.requestDedupe.deduped(apiName, { payload, options }, (): Promise<any> => (options
      ? ApiConnector.cloudRun(apiName, payload, options)
      : ApiConnector.cloudRun(apiName, payload)));
  }

  updateCookies(): void {
    this.resolveInstallationId();
    if (this.isUserLoggedIn) {
      this.cookieService.setUser(this.getCurrentUser());
    }
    this.cookieService.set('Parse/myAppId/url', this.windowRef.nativeWindow.location.hostname);
  }

  resolveInstallationId(): string {
    const cookieInstallationId = this.cookieService.get('installationId');
    const localStorageInstallationId = this.localStorage.getValue('Parse/myAppId/installationId');
    const installationId = cookieInstallationId || localStorageInstallationId || uuid();
    if (localStorageInstallationId !== installationId) {
      this.localStorage.setValue('Parse/myAppId/installationId', installationId);
    }
    return installationId;
  }

  /**
   * Fetcfhes the session token from cookie and tries to login the user from that if user s not logged in.
   * @returns {Promise<any>}
   */
  async updateUserFromCookies(): Promise<any> {
    if (!this.cookieService.get('token')) return;
    if (this.isUserLoggedIn && this.cookieService.get('token') === this.getCurrentUser().getSessionToken()) {
      this.cookieService.delete('token');
      return;
    }
    const sessionToken = this.cookieService.get('token');
    try {
      await this.loginWithTokenAndUpdateUserInfo(sessionToken);
      this.updateCookies();
      await this.redirectToLastKnowLocation();
      this.windowRef.nativeWindow.location.reload();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
    } finally {
      this.cookieService.delete('token');
    }
  }

  async logout(): Promise<void> {
    await ApiConnector.userLogout();
    this.cookieService.deleteAll();
    this.localStorage.clear();
  }

  /**
   * Logins in the user with the sessionToken and update the user data after login.
   * Calls the 'NOTIFY_LOGIN' broadcast which performs all the user object dependent process.
   * @param {string} sessionToken
   * @param {{loginSource?: string}} userData
   * @returns {Promise<void>}
   */
  async loginWithTokenAndUpdateUserInfo(sessionToken: string, userData: { loginSource?: string } = {}): Promise<void> {
    try {
      await ApiConnector.become(sessionToken, { sessionToken });
      const user = this.getCurrentUser();
      // Refresh the cached acting user so a re-login without a full page reload can't leave it stale.
      this.user = this.getActingUser();
      this.localStorage.delete('CureSkin/mobileNumber');
      this.cookieService.setUser(user);
      await this.updateUserInformationAfterLogin(userData);
      this.broadcast.broadcast('NOTIFY_LOGIN');
      return Promise.resolve();
    } catch (error) {
      this.cookieService.delete('token');
      return Promise.reject(error.toString());
    }
  }

  /**
   * If user preferred language is empty or different from what he is on,
   * then it adds the new language code to user data and calls to update that also.
   * @param {{loginSource?: string}} userData
   * @returns {Promise<void>}
   */
  async updateUserInformationAfterLogin(userData: { loginSource?: string } = {}): Promise<void> {
    const user = this.getCurrentUser();
    const data: any = { ...userData };
    const currentLanguage = this.currentWebSiteLanguage;
    if (user && !user.get('languagePreference')) data.languagePreference = currentLanguage;
    else if (user && user.get('languagePreference') !== currentLanguage) data.languagePreference = currentLanguage;
    await this.updateUserData(data);
  }

  /**
   * Updates experimental flags of user in user table 'experimentLogs' (column).
   * @param {object} data - list of flagnames with boolean|string value denoting status of flag.
   */
  updateExperimentLogsInUser(data: { [key: string]: string | boolean }): void {
    const user = this.getActingUser();
    if (!user) return;
    const experimentLogs = user.get('experimentLogs') ?? {};
    Object.keys(data).forEach((key: string): void => {
      experimentLogs[key] = data[key] ?? true;
    });
    void this.updateUserData({ experimentLogs });
  }

  /**
   * Updates the data of user in user table.
   * @param {object} userData - list of key|value to be updated in user table.
   * @returns {Promise<any>}
   */
  async updateUserData(userData: Record<string, unknown>): Promise<any> {
    if (!this.isUserLoggedIn || !Object.keys(userData).length || this.isInternalUser()) return;
    if (userData?.languagePreference) {
      this.getAppBridgeService().changeLanguage(userData.languagePreference as string);
    }
    const { sessionToken }: { sessionToken: string } = this.localStorage.getJsonValue('Parse/myAppId/currentUser');
    const user = await this.getActingUser();
    Object.keys(userData).forEach((key: string): void => {
      if (userData[key] === undefined) {
        user.unset(key);
        return;
      }
      user.set(key, userData[key]);
    });
    await user.save();
    if (!sessionToken) return;
    const localStorageUser = this.localStorage.getJsonValue('Parse/myAppId/currentUser');
    localStorageUser.sessionToken = sessionToken;
    this.localStorage.setJsonValue('Parse/myAppId/currentUser', localStorageUser);
  }

  isInternalUser(): boolean {
    if (!this.isUserLoggedIn) return false;
    return !!this.getCurrentUser().get('type');
  }

  initiateOrderPayment(payload_: {
    channel?: string;
    orderId: string;
    gateway: string;
    paymentType: string;
    paymentId?: string;
  }): Promise<InitiateOrderPaymentResponse> {
    return this.apiWall(async (): Promise<InitiateOrderPaymentResponse> => {
      const response = await this.serverApi.initiateOrderPayment(
        this.getSessionToken(),
        payload_.orderId,
        {
          ...(payload_.channel && { channel: payload_.channel as ExternalChannel }),
          paymentGateway: payload_.gateway,
          paymentType: payload_.paymentType,
          uniqueId: uuid(),
        },
      );
      return response.data;
    });
  }

  /**
   * Registers a server-side regimen mutation so subsequent regimen fetches wait for it to settle.
   * Failures are swallowed here only for sequencing; callers still receive the original rejection.
   */
  private trackRegimenMutation<T>(request: Promise<T>): Promise<T> {
    this.pendingRegimenMutation = request.catch((): void => undefined);
    return request;
  }

  /**
   * Write-through: syncs a locally-mutated regimen into the localStorage cache (map-replace by regimenId).
   * Keeps cache-served reads consistent with changes already applied in memory/store.
   */
  updateRegimenInLocalCache(updatedRegimen: AllocatedRegimen): void {
    const user = this.getActingUser();
    const regimens = this.localStorage.getRegimens(user);
    if (!regimens.length) return;
    const updatedRegimens = regimens.map((regimen: AllocatedRegimen): AllocatedRegimen => (
      regimen.regimenId === updatedRegimen.regimenId ? { ...regimen, ...updatedRegimen } : regimen
    ));
    this.localStorage.setRegimens(updatedRegimens, user);
  }

  /**
   * Updates the regimen in localstorage for faster response on next time.
   * 1. Compare the local regimen with fetched regimen. It uses only ['morning', 'night'] keys of regimen to compare changes.
   * @param {boolean} notifyUpdate - if true, checks is regimen is updated and sets flag in localstorage.
   * @returns {Promise<Array<Entity['Regimen']>>}
   */
  updateRegimensLocally(notifyUpdate?: boolean): Promise<Array<AllocatedRegimen>> {
    return this.apiWall(async (): Promise<Array<AllocatedRegimen>> => {
      await this.pendingRegimenMutation;
      const jsonRegimens = await this.getAllocatedRegimens();
      const jsonRegimensCopy: Array<AllocatedRegimen> = JSON.parse(JSON.stringify(jsonRegimens));
      const localRegimen = this.localStorage.getRegimens(this.getActingUser());
      this.localStorage.setRegimens(jsonRegimens, this.getActingUser());
      if (notifyUpdate) {
        const keysToCompare = ['morning', 'night'];
        localRegimen.forEach((each_: AllocatedRegimen): void => {
          const each = each_;
          Object.keys(each_).forEach((key: string): void => {
            if (!keysToCompare.includes(key)) delete each[key];
          });
        });
        jsonRegimensCopy.forEach((each_: AllocatedRegimen): void => {
          const each = each_;
          Object.keys(each_).forEach((key: string): void => {
            if (!keysToCompare.includes(key)) delete each[key];
          });
        });
        const isRegimenChanged = JSON.stringify(localRegimen).localeCompare(JSON.stringify(jsonRegimensCopy));
        if (isRegimenChanged !== 0) this.localStorage.setValue('regimenUpdateVisited', false);
        else {
          this.allowRegimenForceFetch = false;
          setTimeout((): boolean => this.allowRegimenForceFetch = true, this.regimenRefreshInterval);
        }
      }
      return jsonRegimens;
    }).catch((): Array<AllocatedRegimen> => this.localStorage.getRegimens(this.getActingUser()));
  }

  private updateRegimenProductPointerWithObjects(regimen: Entity['Regimen'], products: Array<Models.Catalog>): Entity['Regimen'] {
    const productMap: Record<string, Models.Catalog> = {};
    (products ?? []).forEach((each) => (productMap[each.objectId] = each));
    return {
      ...regimen,
      morning: this.attachProductDetailsToRegimenMorningNightSection(regimen.morning, productMap),
      night: this.attachProductDetailsToRegimenMorningNightSection(regimen.night, productMap),
      variants: (regimen.variants ?? []).map((each) => ({
        ...each,
        morning: this.attachProductDetailsToRegimenMorningNightSection(each.morning, productMap),
        night: this.attachProductDetailsToRegimenMorningNightSection(each.night, productMap),
      }))
    };
  }

  private attachProductDetailsToRegimenMorningNightSection(
    items: Entity['Regimen']['morning'],
    productMap: Record<string, Models.Catalog>): Entity['Regimen']['morning'] {
    return (items ?? []).map((each) => ({
      ...each,
      product: productMap[each.product.objectId] ?? each.product
    }))
  }

  async getAllocatedRegimens(): Promise<Array<AllocatedRegimen>> {
    const response = await this.serverApi.getAllocatedRegimens(this.getSessionToken(), this.getActingUser().id);
    const rows = response.data.data ?? [];
    return rows.map((row): AllocatedRegimen => ({
      ...(this.updateRegimenProductPointerWithObjects(row.regimen, row.products) as unknown as AllocatedRegimen),
      products: row.products ?? [],
      orderPlaced: row.order?.placed ?? false,
      deliveredDate: row.order?.deliveredDate,
      delivered: row.order?.deliveredDate !== undefined,
    }));
  }

  // Caching decision (explicit-cache-decision): AI assistant messages are real-time
  // conversation state — never cached. No withCache; every call hits the server.
  sendAiAssistantMessage(
    request: SendAiAssistantMessageRequest & Pick<AiAssistantMessage, 'state' | 'turn' | 'interactionId'>,
  ): Promise<AiAssistantMessage[]> {
    return this.apiWall(async (): Promise<AiAssistantMessage[]> => {
      const response = await this.serverApi.sendAiAssistantMessage(request, this.getSessionToken());
      return response.data.data ?? [];
    });
  }

  // Caching decision (explicit-cache-decision): not cached — the conversation is polled
  // for new messages, so responses must always be fresh.
  listAiAssistantMessages(timestamp?: string, ascending?: boolean, limit?: number): Promise<AiAssistantMessage[]> {
    return this.apiWall(async (): Promise<AiAssistantMessage[]> => {
      const response = await this.serverApi.listAiAssistantMessages(this.getSessionToken(), timestamp, ascending, limit);
      return response.data.data ?? [];
    });
  }

  // Caching decision (explicit-cache-decision): not cached — used by the poll loop to
  // re-read a single message's live failure state (requestId/objectId), must be fresh.
  getAiAssistantMessage(messageId: string): Promise<AiAssistantMessage | null> {
    return this.apiWall(async (): Promise<AiAssistantMessage | null> => {
      const response = await this.serverApi.getAiAssistantMessage(messageId, this.getSessionToken());
      return response.data.data ?? null;
    });
  }

  // Caching decision (explicit-cache-decision): mutation — re-runs the assistant for a
  // failed user message; never cached.
  retryAiAssistantMessage(messageId: string): Promise<AiAssistantMessage[]> {
    return this.apiWall(async (): Promise<AiAssistantMessage[]> => {
      const response = await this.serverApi.retryAiAssistantMessage(messageId, this.getSessionToken());
      return response.data.data ?? [];
    });
  }

  /**
   * Fetches the regimen from api or serves local cache of regimens from localstorage.
   * 1. Calls api to fetch for latest regimen if cache is served.
   * 2. 'allowRegimenForceFetch' boolean controls the frequency of regimen fetch.
   *    Even if its force fetch, fetches only after every 2 mins. Time can customized as per need to avoid frequent fetch noise.
   * 3. 'byPassLocalFetch' to bypass the local fetch of regimen altogether.
   * @param username
   * @param regimenId
   * @param {boolean} force - is to ignore local caches and fetch from api.
   * @returns {Promise<Array<AllocatedRegimen>>}
   */
  // Note: P0 - use interface so that we can pass named parameters
  fetchRegimens(regimenId?: string | null, force?: boolean, byPassLocalFetch?: boolean,
    include: ParseKeys<Table.Regimen>[] = []): Promise<Array<AllocatedRegimen>> {
    this.store.dispatch(fromActions.RegimenStateUpdate({ isFetchInProgress: true }));
    return this.apiWall(async (): Promise<Array<AllocatedRegimen>> => {
      const regimens = this.localStorage.getRegimens(this.getActingUser());
      if (!regimens.length || (force && this.allowRegimenForceFetch) || byPassLocalFetch) {
        try {
          return this.updateRegimensLocally(true);
        } catch (err) {
          return Promise.resolve(regimens);
        }
      }
      void this.updateRegimensLocally(true);
      return Promise.resolve(regimens);
    }).then((regimens: unknown): Array<AllocatedRegimen> => {
      const regimenArray: Array<AllocatedRegimen> = Array.isArray(regimens) ? regimens : [];
      const isLocalLookup = !include || include.length === 0;
      const storeAlreadyPopulated = this.store.selectSignal(fromSelectors.selectRegimens)().length > 0;
      if (isLocalLookup && storeAlreadyPopulated) {
        this.store.dispatch(fromActions.RegimenStateUpdate({ isFetchInProgress: false }));
        return regimenArray;
      }
      this.store.dispatch(fromActions.RegimenStateUpdate({
        regimens: regimenArray,
        regimensFetched: true,
        isFetchInProgress: false,
      }));
      return regimenArray;
    });
  }

  reSendOTP(mobileNumber: string,
    params: Omit<LoginRetryRequest, 'mobileNumber' | 'time' | 'digest'> = {}): Promise<LoginRetryResponse> {
    return this.apiWall(async (): Promise<LoginRetryResponse> => {
      const time = new Date().toISOString();
      const digest = await generateDigest(mobileNumber, time);
      const request: LoginRetryRequest = { mobileNumber, time, digest, ...params };
      const response = await this.serverApi.loginRetry(request);
      return response?.data;
    });
  }

  verifyOTP(payload_: AuthConfig): Promise<LoginVerifyResponse> {
    return this.apiWall(async (): Promise<LoginVerifyResponse> => {
      const common = {
        aaid: payload_.aaid,
        deviceId: payload_.deviceId || this.cookieService.get('deviceId') || this.uniqueId,
        installationId: this.resolveInstallationId(),
        referral: payload_.referralCode,
        source: payload_.source,
      };
      let request: LoginVerifyRequest;
      if (payload_.loginMethod === TruecallerV2VerifyRequestLoginMethodEnum.truecaller_v2) {
        request = {
          ...common,
          loginMethod: payload_.loginMethod,
          authCode: payload_.authCode,
          codeVerifier: payload_.codeVerifier,
        };
      } else {
        const time = new Date().toISOString();
        request = {
          ...common,
          loginMethod: payload_.loginMethod,
          mobileNumber: payload_.mobileNumber,
          OTP: payload_.OTP,
          digest: await generateDigest(payload_.mobileNumber, time),
          time,
        };
      }
      const response = await this.serverApi.loginVerify(request);
      return response?.data;
    });
  }

  allocateRegimen(regimenId:string, context:{}): Promise<any> {
    const username = this.getActingUser().get('username');
    return this.apiWall((): Promise<any> => this.cloudRun('findOrCreateAndSendPersonalizedCopy', { regimenId, username, context }));
  }

  requestOTP(userMobileNumber: string, source: string,
    params: Omit<OTPGenerateRequest, 'mobileNumber' | 'source' | 'deviceId' | 'time' | 'digest'> = {},
  ): Promise<LoginGenerateOtpResponse> {
    return this.apiWall(async (): Promise<LoginGenerateOtpResponse> => {
      const deviceId = this.cookieService.get('deviceId') || this.uniqueId;
      const time = new Date().toISOString();
      const digest = await generateDigest(userMobileNumber, time);
      const request: OTPGenerateRequest = { mobileNumber: userMobileNumber, source, deviceId, time, digest, ...params };
      const response = await this.serverApi.loginGenerateOtp(request);
      return response?.data;
    });
  }

  fetchInstantCheckupByImageUrl(user: any, url: string): Promise<any> {
    return this.apiWall((): Promise<any> => {
      const where = { user, imagePath: url };
      return ApiConnector.findOne(Table.InstantCheckup, { where, project: ['objectId', 'imagePath'] });
    });
  }

  async fetchFAQ(objectId?: any): Promise<any> {
    return this.apiWall((): Promise<any> => {
      const fetchFaq = withCache(
        'fetchFAQ',
        (params) => this.cloudRun('fetchFaqWithCategories', params),
      );
      return fetchFaq({ where: { objectId } });
    });
  }

  async disablePreviousConsultationSessions(concernClass?: string): Promise<any> {
    return this.apiWall(async (): Promise<any> => {
      const username = this.getActingUser().get('username');
      const result = await this.cloudRun('restartTree', { username, class: concernClass });
      if (result.status === 'success') {
        return this.updateUserData({ PrivateMainConcern: undefined, PrivateMainConcernClass: undefined });
      }
      return Promise.resolve();
    });
  }

  async saveUserMainConcern(concerns: Array<any>): Promise<any> {
    return this.apiWall(async (): Promise<any> => {
      await this.updateUserData({ concernList: concerns, PrivateMainConcern: concerns[0] });
    });
  }

  async saveUserSecondaryConcerns(concerns: Array<string>): Promise<any> {
    return this.apiWall(async (): Promise<any> => {
      await this.updateUserData({ concernList: concerns });
    });
  }

  async findUserActiveExperiments(_username?: string): Promise<any> {
    let username = _username;
    if (!_username) {
      username = this.getActingUser()?.get('username');
    }
    if (!this.isUserLoggedIn) return Promise.resolve([]);
    const activeExperiments = this.localStorage.getExperiments(this.getActingUser());
    if (activeExperiments.length) return activeExperiments;

    return this.apiWall(async (): Promise<any> => {
      try {
        const userActiveExperiments: Array<any> = (await this.cloudRun('findUserActiveExperiments', { username })) as Array<any>;
        const userActiveExperimentsJSONArray = JSON.parse(JSON.stringify(userActiveExperiments));
        this.store.dispatch(fromActions.ExperimentsUpdate({ experiments: userActiveExperimentsJSONArray }));
        this.localStorage.setExperiments(userActiveExperimentsJSONArray, this.getActingUser());
        return userActiveExperimentsJSONArray;
      } catch (e) {
        return this.localStorage.getJsonValue('CureSkin/activeExperiments', '[]');
      }
    });
  }

  async findMainConcernsOptions(where: object): Promise<any> {
    return this.apiWall((): Promise<any> => this.cloudRun('fetchMainConcern', { payload: where }));
  }

  async findMainWithSecondaryConcerns(payload:
    { concernClass: Entity['Regimen']['class']; shouldTranslateConcernsList: boolean }): Promise<any[]> {
    try {
      const sessionToken = this.getSessionToken();

      if (!sessionToken) {
        return [];
      }

      const response = await this.apiWall(
        (): Promise<any> => this.serverApi.getMainConcerns(payload.concernClass, payload.shouldTranslateConcernsList, sessionToken));

      return response?.data?.mainConcerns ?? [];
    } catch (error) {
      console.error('Error fetching main concerns:', error);
      return [];
    }
  }

  createConsultationOrder(amount: number): Promise <any> {
    return this.apiWall(async (): Promise<any> => {
      const oldOrder = await ApiConnector.findOne(Table.Order, {
        where: {
          type: this.appConfig.Shared.Order.Type.CONSULTATION,
          amount,
          stage: this.appConfig.Shared.Order.Stage.ONLINE_PAYMENT_PENDING,
        },
        descending: 'createdAt',
      });
      if (oldOrder) {
        return oldOrder;
      }
      const order = new Table.Order();
      order.set('type', this.appConfig.Shared.Order.Type.CONSULTATION);
      order.set('amount', amount);
      order.set('noMessageToUser', true);
      order.set('user', this.getActingUser());
      order.set('stage', this.appConfig.Shared.Order.Stage.INITIAL);
      order.set('paymentType', this.appConfig.Shared.Order.PaymentType.ONLINE);
      return order.save();
    });
  }

  async findAllProductsForUser(tag?: Array<any>, responseArray?: boolean, excludeTag?: string[]): Promise<any> {
    return this.apiWall((): Promise<any> => {
      const findAllProductsForUser = withCache(
        'findAllProductsForUser',
        (params) => this.cloudRun('findAllProductsForUser', params),
      );
      return findAllProductsForUser({
        username: this.getActingUser().get('username'),
        tag,
        responseArray,
        excludeTag,
      });
    });
  }

  async findProductsForUser(payload: {
    tag?: Array<any>,
    responseArray?: boolean, excludeTag?: string[], limit?: number
  }): Promise<any> {
    return this.apiWall((): Promise<any> => {
      const findProductsForUser = withCache(
        'findProductsForUser',
        (params) => this.cloudRun('findAllProductsForUser', params),
      );
      return findProductsForUser({
        username: this.getActingUser().get('username'),
        tag: payload.tag,
        responseArray: payload.responseArray,
        excludeTag: payload.excludeTag,
        ...(payload.limit && { limit: payload.limit }),
      });
    });
  }

  async getAllMergedProductCollection(): Promise<Array<Models.Catalog>> {
    const sessionToken = this.getSessionToken();
    const userLanguage = this.getUserLanguage;
    return this.apiWall(async (): Promise<Array<Models.Catalog>> => {
      if (!sessionToken) {
        return [];
      }
      const getAllMergedProductCollection = withCache(
        `getAllMergedProductCollection-${userLanguage}`,
        (token) => this.serverApi.getAllMergedProductCollection(token),
      );
      const response: any = await getAllMergedProductCollection(sessionToken);
      return response?.data?.productCollection?.products ?? [];
    });
  }

  cachedFnWrapper<T, V>(
    cb: (...args: T[]) => V,
    key: string,
  ): (...args: T[]) => Promise<V> {
    return async (...args: T[]): Promise<V> => {
      let result: V;
      const cachedValue = this.cacheService.retrieve(key);
      if (cachedValue) {
        result = cachedValue;
      } else {
        result = await this.executeCallbackAndCacheResult((): V => cb(...args), key);
      }
      return result;
    };
  }

  private async executeCallbackAndCacheResult<T, V>(
    cb: (...args: T[]) => V,
    key: string,
  ): Promise<V> {
    let result = await cb();
    result = JSON.parse(JSON.stringify(result));
    this.cacheService.store(key, result);
    return result;
  }

  findNextFollowUp(): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.findOne(Table.FollowUp, {
      where: { user: this.getActingUser(), State: ['WAITING_FOR_IMAGE', 'PENDING'] },
      descending: 'createdAt',
    }));
  }

  findBodyProds(): Promise<any> {
    return this.apiWall(async (): Promise<any> => {
      const findBodyProds = withCache(
        'findBodyProds',
        (params) => ApiConnector.find(Table.Catalog, params),
      );
      return findBodyProds({
        where: { marketingTags: { $in: 'BODY' }, isCommonAddon: true },
      });
    });
  }

  findSaleProds(limit?: number): Promise<any> {
    return this.apiWall(async (): Promise<any> => {
      const findSaleProds = withCache(
        'findSaleProds',
        (params) => ApiConnector.find(Table.Catalog, params),
      );
      return findSaleProds({
        where: { isSaleProduct: true },
        limit,
      });
    });
  }

  findBogoSaleProds(limit?: number): Promise<any> {
    const productProjectFields = [
      'product.title',
      'product.titleLanguageString',
      'product.mrp',
      'product.price',
      'product.ranking',
      'product.inventoryStatus',
      'product.objectId',
      'product.rebrandedImageWithoutBackground',
      'product.rebrandedImageWithBackground',
      'product.productUnboxedImage',
      'product.alternateProduct',
    ];
    return this.apiWall(async (): Promise<any> => {
      const findBogoSaleProds = withCache(
        'findBogoSaleProds',
        (params) => ApiConnector.find(Table.AddOnProduct, params),
      );
      return findBogoSaleProds({
        where: { user: this.getActingUser(), isBogoProduct: true },
        include: ['product'],
        project: productProjectFields,
        limit,
      });
    });
  }

  findKitsProducts(limit?: number): Promise<any> {
    return this.apiWall(async (): Promise<any> => {
      const findKitsProducts = withCache(
        'findKitsProducts',
        (params) => ApiConnector.find(Table.Catalog, params),
      );
      return findKitsProducts({
        where: { isKitProduct: true },
        limit,
      });
    });
  }

  findHoliSaleProds(limit?: number): Promise<any> {
    return this.apiWall(async (): Promise<any> => {
      const findHoliSaleProds = withCache(
        'findHoliSaleProds',
        (params) => ApiConnector.find(Table.Catalog, params),
      );
      return findHoliSaleProds({
        where: { isHoliProduct: true },
        limit,
      });
    });
  }

  fetchProductCollection(name: string[] | string): Promise<any> {
    // `recommendedProducts` needs a productLimit and a default "Just for you"
    // title, so route it through fetchRecommendedProducts.
    if (name === 'recommendedProducts') return this.fetchRecommendedProducts(20);
    const sessionToken = this.getSessionToken();
    const userLanguage = this.getUserLanguage;
    return this.apiWall(async (): Promise<any> => {
      const collectionKey = Array.isArray(name) ? name.join('-') : name;
      const cacheKey = `ProductCollection-${userLanguage}-${collectionKey}`;
      const fetchProductCollection = withCache(
        cacheKey,
        (params) => this.serverApi.findProductCollectionByName(params, sessionToken),
      );
      return (await fetchProductCollection(name)).data?.productCollection;
    });
  }

  /**
   * Fetches the personalised "Recommended Products" collection ("Just for you" section).
   * Uses the same collection endpoint as the shop page, passing `productLimit`.
   */
  fetchRecommendedProducts(productLimit: number = 10): Promise<any> {
    const sessionToken = this.getSessionToken();
    const userLanguage = this.getUserLanguage;
    return this.apiWall(async (): Promise<any> => {
      const cacheKey = `ProductCollection-${userLanguage}-recommendedProducts-${productLimit}`;
      const fetchRecommendedProducts = withCache(
        cacheKey,
        (limit) => this.serverApi.findProductCollectionByName('recommendedProducts', sessionToken, limit),
      );
      const collection = (await fetchRecommendedProducts(productLimit)).data?.productCollection;
      if (collection && !collection.title) collection.title = 'Just for you';
      return collection;
    });
  }

  findUserCallStatus(): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.findOne(Table.PendingCall, {
      where: {
        user: this.getActingUser(),
        status: { $eq: 'CallCompleted' },
        createdAt: { $gte: this.timeService.removeMonths(new Date(), 4) },
      },
      project: ['status'],
    }),
    );
  }

  async findOnePendingCall(payload: RequestQueryPayload<Table.PendingCall>): Promise<any> {
    return this.apiWall(async (): Promise<any> => {
      const findOnePendingCall = withCache(
        'findOnePendingCall',
        (params) => ApiConnector.findOne(Table.PendingCall, params),
      );
      return findOnePendingCall({
        where: payload.where,
        project: payload.project,
      });
    })
  }

  async getCountOfUserPendingCall(payload: RequestQueryPayload<Table.PendingCall>): Promise<number> {
    return ApiConnector.count(Table.PendingCall, payload);
  }
  findUserReacquisitionCall(): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.findOne(Table.PendingCall, {
      where: {
        user: this.getActingUser(),
        type: this.appConfig.Shared.PendingCall.Type.RE_ACQUISITION_CALL,
        requestTime: { $gte: this.timeService.removeMonths(new Date(), 4) },
      },
      project: ['status'],
    }));
  }

  createPendingCallForUser(): Promise <any> {
    return this.apiWall(async (): Promise<any> => {
      const pendingCall = new Table.PendingCall();
      const user = this.getActingUser();
      pendingCall.set('type', this.appConfig.Shared.PendingCall.Type.UNPAID_USER_DOCTOR_CALL);
      pendingCall.set('user', user);
      pendingCall.set('status', 'Requested');
      pendingCall.set('teams', ['doctor']);
      pendingCall.set('uniqueId', `unpaid_user_doctor_call_${user.id}`);
      return pendingCall.save();
    });
  }

  getPinCodeInfo(pinCode: number, _context: Record<string, string> = {}): Promise<PinCodes | null> {
    return this.apiWall(async (): Promise<PinCodes | null> => {
      const res: AxiosResponse<GetPinCodeInfoResponse> = await this.serverApi.getPinCodeInfo(
        String(pinCode),
        this.getSessionToken(),
      );
      return res?.data?.pinCode ?? null;
    });
  }

  public async updateLocalUser(): Promise<boolean> {
    if (!this.isUserLoggedIn) return false;
    try {
      await this.apiWall(async (): Promise<any> => this.updateCurrentLocalUser());
    } catch (err) {
      if (err.code === 209 || err.code === 206) {
        void this.logout();
        await this.router.navigate(['/onboarding/number']);
        return false;
      }
      const errorName = `iosWhiteScreenIssueUpdateUser${err.code}`;
    }
    return true;
  }

  async updateCurrentLocalUser(): Promise<any> {
    const { sessionToken }: { sessionToken: string } = this.localStorageService.getJsonValue('Parse/myAppId/currentUser');
    let user = ApiConnector.getCurrentUser();
    if (!user) return user;
    user = await ApiConnector.getCurrentUser().fetch();
    if (!sessionToken) return user;
    const localStorageUser = this.localStorageService.getJsonValue('Parse/myAppId/currentUser');
    localStorageUser.sessionToken = sessionToken;
    this.localStorageService.setJsonValue('Parse/myAppId/currentUser', localStorageUser);
    return user;
  }

  fetchOrderStages(forceFetch?: boolean): Promise<Array<Entity['OrderStage']>> {
    if (!forceFetch) {
      const localOrderStages = this.localStorage.getJsonValue('CureSkin/orderStages', '[]');
      if (localOrderStages.length) return Promise.resolve(localOrderStages);
    }
    return this.apiWall(async (): Promise<Array<Entity['OrderStage']>> => {
      const response = await this.serverApi.getOrderStages(this.getSessionToken());
      const orderStages: Array<Entity['OrderStage']> = response.data.data ?? [];
      const orderStageMap = orderStages.reduce((r, each) => ({
        ...r,
        [each.objectId]: { ...each },
      }), {} as Record<string, Entity['OrderStage']>);
      orderStages.forEach((stage) => {
        stage.possibleStageChange = stage.possibleStageChange.map((each) => ({ ...orderStageMap[each.objectId] }));
      });
      this.localStorage.setJsonValue('CureSkin/orderStages', orderStages);
      return orderStages;
    });
  }

  async findUserByObjectId(userObjectId?: string, force?: boolean, context?: any): Promise<any> {
    return this.apiWall(async (): Promise<any> => {
      const objectId = userObjectId || this.getActingUser()?.id;
      const userHash: any = this.dataStore.get('USERS');
      if (!force && userHash[objectId]) {
        return userHash[objectId];
      }
      const user = await ApiConnector.findOne(Table.User, {
        where: { objectId: userObjectId },
        option: { context } as Parse.FullOptions,
      }) as Table.User & {isPaid:boolean};
      userHash[objectId] = user;
      if (user) user.isPaid = this.isPaid.call(user, this);
      this.dataStore.set('USERS', userHash);
      return user;
    });
  }

  async findUserPropertyByObjectId(userObjectId?: string, project: string[] = []): Promise<any> {
    return ApiConnector.findOne(Table.User, { where: { objectId: userObjectId }, project });
  }

  async findUserConfirmationsPending(id?: any): Promise<any> {
    return this.apiWall(async (): Promise<any> => {
      const where = { userConfirmed: false, user: this.getActingUser(), objectId: id };
      if (!id) delete where.objectId;
      return ApiConnector.findOne(Table.UserConfirmation, { where, descending: 'createdAt' });
    });
  }

  getOrders(stages?: Array<Entity['Order']['stage']>): Promise<Array<Entity['Order']>> {
    return this.apiWall(async (): Promise<Array<Entity['Order']>> => {
      const getOrders = withCache(
        'getOrders',
        async (params: { stages?: Array<Entity['Order']['stage']>; userId?: string }) => {
          const result = await this.serverApi.listUserOrders(
            this.getSessionToken(),
            1,
            50,
            'createdAt',
            params.stages ? params.stages.join(',') : undefined,
            params.userId);
          return result.data.orders;
        }
      );
      return getOrders({
        stages,
        userId: this.isInternalUser() ? this.getActingUser().id : undefined,
      });
    });
  }

  fetchOrders(where: object = {},
    project: any = [],
    include: any = ['products', 'services', 'regimen', 'regimen.products' as 'regimen', 'coupons', 'user',
      'regimenStateAtLastCheckpoint'],
    limit: number = 50): Promise<any[]> {

    return this.apiWall(async (): Promise<any> => {
      const fetchOrders = withCache(
        'fetchOrders',
        (params) => ApiConnector.find(Table.Order, params),
      );
      return fetchOrders({ where, project, descending: 'createdAt', include, limit });
    });
  }

  cancelOrder(orderId: string, cancelReason?: string): Promise<void> {
    resetCacheByName('fetchOrders');
    resetCacheByName('getOrders');
    return this.apiWall(async (): Promise<void> => {
      await this.serverApi.cancelOrder(this.getSessionToken(), orderId, { cancelReason });
    });
  }

  async updateRegimenService(regimenId: string, serviceType: any): Promise<any> {
    return this.apiWall(async (): Promise<any> => this.cloudRun('updateRegimenService', { regimenId, serviceType }));
  }

  async applyDiscountWith(couponCode: string, orderId: string): Promise<void> {
    return this.apiWall(async (): Promise<void> => {
      await this.serverApi.applyCouponToOrder(this.getSessionToken(), orderId, { couponCode });
    });
  }

  async removeDiscountCoupon(couponCode: string, orderId: string): Promise<void> {
    return this.apiWall(async (): Promise<void> => {
      await this.serverApi.removeCouponFromOrder(this.getSessionToken(), orderId, { couponCode });
    });
  }

  async findRegimenById(id: any): Promise<AllocatedRegimen> {
    return this.apiWall(async (): Promise<AllocatedRegimen> => {
      const regimens = await this.fetchRegimens(undefined, true);
      return regimens.find((each) => (each.objectId === id));
    });
  }

  async findRegimenByRegimenId(regimenId: string): Promise<AllocatedRegimen> {
    return this.apiWall(async (): Promise<AllocatedRegimen> =>  {
      const regimens = await this.fetchRegimens(undefined, true);
      const regimen = regimens.find((each) => (each.regimenId === regimenId));;
      return JSON.parse(JSON.stringify(regimen));
    });
  }

  async fetchAssistantById(id: any, imageCompareLinkFlag?: boolean): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.findOne(Table.Assistant, { where: { objectId: id, imageCompareLinkFlag } }));
  }

  async fetchConfig(): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.findOne(Table.Config, { where: {} }));
  }

  findProductsById(productId: Array<any>, include?: any): Promise<any> {
    return this.apiWall(async (): Promise<any> => {
      const findProductsById = withCache(
        'findProductsById',
        (params) => ApiConnector.find(Table.Catalog, params),
      );
      return findProductsById({ where: { objectId: productId }, include });
    });
  }

  findCatalogWithKey(productId: Array<any>): Promise<any> {
    return this.apiWall(async (): Promise<any> => {
      const findCatalogWithKey = withCache(
        'findProductsById',
        (params) => ApiConnector.find(Table.Catalog, params),
      );
      return findCatalogWithKey({ where: { objectId: productId } });
    });
  }

  deleteInstantCheckup(instantCheckupObjectId: any, confirm?: boolean): Promise<any> {
    return this.apiWall(async (): Promise<any> => this.cloudRun('deleteInstantCheckUp',
      { instantCheckupObjectId, confirm }));
  }

  fetchShopProducts(): Promise<any> {
    return this.apiWall(async (): Promise<any> => this.cloudRun('findAllShopProducts', {
      username: this.getActingUser().get('username'),
      responseArray: true,
    }));
  }

  async updateOrderBeforeShipment(params: { id: string; addressBookId: string }): Promise<void> {
    return this.apiWall(async (): Promise<void> => {
      await this.serverApi.updateOrderDeliveryAddress(
        this.getSessionToken(),
        params.id,
        { addressBookId: params.addressBookId },
      );
    });
  }

  async findOrderWithSignedURL(id: any): Promise<Entity['Order']> {
    return this.findOrderId(id);
  }

  async findOrderId(id: any): Promise<Entity['Order']> {
    return this.apiWall(async (): Promise<Entity['Order']> => {
      const response = await this.serverApi.getUserOrderById(this.getSessionToken(), id);
      return response.data.order;
    });
  }

  async findOrderStages(where: { order?: { id?: string; objectId?: string } } = {}): Promise<Array<Entity['OrderConsultationLog']>> {
    return this.apiWall(async (): Promise<Array<Entity['OrderConsultationLog']>> => {
      const orderId = where.order?.id ?? where.order?.objectId;
      if (!orderId) return [];
      const response = await this.serverApi.getOrderStageLogs(orderId, this.getSessionToken());
      return response.data?.data ?? [];
    });
  }

  async findConsultationSessionChat(payload: RequestQueryPayload<Table.ConsultationSessionChat>): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.find(Table.ConsultationSessionChat, payload));
  }

  async findFollowUpChat(payload: RequestQueryPayload<Table.FollowUpChat>): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.find(Table.FollowUpChat, payload));
  }

  async findOrderChat(payload: RequestQueryPayload<Table.OrderChat>): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.find(Table.OrderChat, payload));
  }

  async findOneOrderChat(payload: RequestQueryPayload<Table.OrderChat>): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.findOne(Table.OrderChat, payload));
  }

  async likeFeed(feedId: string): Promise<any> {
    return this.apiWall((): Promise<any> => this.cloudRun('likeFeed', { feedId }));
  }

  fetchPrescription(id: any, regimenId: any): Promise<any> {
    return this.apiWall(async (): Promise<any> => this.cloudRun('fetchPrescription', { id, regimenId }));
  }

  createUserImageUploadUrl(extension: any, username: any, source: string): Promise<any> {
    return this.apiWall(async (): Promise<any> => this.cloudRun('createUserImageUploadUrl',
      { extension, username, source }));
  }

  checkoutAndCreateOrder(payload: any): Promise<any> {
    resetCacheByName('getOrders');
    resetCacheByName('fetchOrders');
    return this.apiWall((): Promise<any> => this.cloudRun('checkoutAndCreateOrder', payload));
  }

  fetchInstantCheckup(payload: { userId?: string,
    id?: string[],
    limit?: number,
    imagePath?: string,
    project?: string[],
    type?: string[],
    sortAscending?: boolean,
    includeUnsignedImagePath?: boolean,
    dateRange?: { startDate?: Date, endDate?: Date } }): Promise<any> {
    return this.apiWall((): Promise<any> => this.cloudRun('previousInstantCheckup', payload));
  }

  async getMainUserPhotos(regimenClass: GetMainUserPhotosRegimenClassEnum): Promise<Array<InstantCheckup>> {
    const sessionToken = this.getSessionToken();
    if (!sessionToken) return [];
    const response = await this.serverApi.getMainUserPhotos(sessionToken, regimenClass);
    return response.data.instantCheckups ?? [];
  }

  async getInstantCheckupsByTypes(
    types: string[], limitPerType?: number, sortAscending: boolean = false,
  ): Promise<GetInstantCheckupsResponse> {
    const sortOrder = sortAscending ? GetInstantCheckupsSortOrderEnum.asc : GetInstantCheckupsSortOrderEnum.desc;
    const response = await this.serverApi.getInstantCheckups(
      this.getSessionToken(), new Set(types) as Set<InstantCheckupTypes>, limitPerType, sortOrder);
    return response.data;
  }

  /** The user's stored answers for the requested questions. Ids that could not be resolved come back in `skipped`. */
  async getQuestionResponses(regimenClass: RegimenClass, questionIds: string[]): Promise<GetQuestionResponsesResponse> {
    const response = await this.serverApi.getQuestionResponses(this.getSessionToken(), regimenClass, new Set(questionIds));
    return response.data;
  }

  /** Stores one answer, overwriting any previous one. Returns what was stored, which may differ from what was sent. */
  async setQuestionResponse(
    regimenClass: RegimenClass, questionId: string, response: string,
  ): Promise<SetQuestionResponseResponse> {
    const result = await this.serverApi.setQuestionResponse(this.getSessionToken(), questionId, regimenClass, { response });
    return result.data;
  }

  /** A question's title and options, translated into the user's language — translate is opt-in on the endpoint. */
  async getQuestionForUser(regimenClass: RegimenClass, uniqueIdentifier: string): Promise<ProjectedQuestionData> {
    const response = await this.serverApi.getQuestion(this.getSessionToken(), uniqueIdentifier, regimenClass, true);
    return response.data.data;
  }

  async allocateRegimenForConsultationSession(
    consultationSessionId: string,
  ): Promise<{ pendingQuestionId?: string, regimenId?: string }> {
    const response = await this.serverApi
      .allocateRegimenForConsultationSession(consultationSessionId, this.getSessionToken());
    return response.data;
  }

  findPaymentById(objectId: string): Promise<any> {
    return this.apiWall((): Promise<any> => ApiConnector.findOne(Table.Payment, {
      where: { objectId },
      include: ['order'],
      project: ['order.amount' as 'order', 'order.type' as 'order'],
    }));
  }

  findSuccessfulNonCODOrderPayments(order: Table.Order): Promise<any> {
    return this.apiWall((): Promise<any> => ApiConnector.find(Table.Payment, {
      where: { PaymentStage: { $in: ApiClientConstant.Payment.SuccessStages },
        order,
        PaymentSource: { $ne: this.appConfig.Shared.Payment.paymentModes.COD } },
    }));
  }

  getArticleById(objectId: any, context?: any): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.findOne(Table.ArticleData, {
      where: { objectId },
      include: ['product', 'params.articleImagesLanguageString' as 'params'],
      option: { context } as Parse.FullOptions,
    }));
  }

  unCancelUserOrder(orderId: any): Promise<any> {
    return this.apiWall(async (): Promise<any> => this.cloudRun('uncancelUserOrder', { orderId }));
  }

  updateLastActiveTime(tabName: string): void {
    if (this.isInternalUser()) return;
    const json = this.localStorage.getJsonValue('CureSkin/lastActiveTime', '{}');
    json[tabName] = { __type: 'Date', iso: new Date() };
    this.localStorage.setJsonValue('CureSkin/lastActiveTime', json);
  }

  async switchUserToState(stateId: string, context: Record<string, unknown> = {}): Promise<any> {
    if (this.isInternalUser()) {
      return undefined;
    }
    return this.apiWall((): Promise<any> => this.cloudRun('switchUserToState', { stateId, context }));
  }

  async fetchRewards(where: any = {}): Promise<any> {
    return this.apiWall((): Promise<any> => ApiConnector
      .find(Table.Reward, { where, include: ['cashTransaction', 'fromUser'], descending: 'createdAt' }));
  }

  async getInstaLiveUpdate(): Promise<any> {
    const where = {};
    return this.apiWall((): Promise<any> => ApiConnector
      .find(Table.InstaLiveReminder, { where, descending: 'createdAt', limit: 1 }));
  }

  async claimReward(rewardId: any, contactDetails?: any): Promise<any> {
    return this.apiWall((): Promise<any> => this.cloudRun('claimReward', { rewardId, contactDetails }));
  }

  async afterFirstRegimenPurchaseOffer(): Promise<any> {
    return this.apiWall((): Promise<any> => this.cloudRun('afterFirstRegimenPurchaseOffer', {}));
  }

  async isAfterPurchaseProductOfferAvailable(): Promise<any> {
    return this.apiWall((): Promise<any> => this.cloudRun('isAfterPurchaseProductOfferAvailable', {}));
  }

  async fetchAssistantTask(assistantId: any): Promise<any> {
    return this.apiWall((): Promise<any> => this.cloudRun('findAssistantTask', { assistantId }));
  }

  async getCureSkinCashTransactions(username?: string, project?: Array<ParseKeys<Table.CashTransaction>>): Promise<any> {
    const user = this.getActingUser();
    return this.apiWall((): Promise<any> => ApiConnector.find(Table.CashTransaction, {
      where: { user },
      include: ['reward', 'order'],
      project,
      descending: 'createdAt',
    }));
  }

  async fetchCashBalance(): Promise<any> {
    return this.apiWall((): Promise<any> => {
      const fetchFaq = withCache(
        'fetchCashBalance',
        (params) => this.cloudRun('fetchCashBalance', params),
      );
      return fetchFaq({ username: this.getActingUser()?.get('username') });
    });
  }

  async updateUserLastSeenTime(): Promise<any> {
    return this.apiWall(async (): Promise<any> => {
      const user = this.getActingUser();
      if (!user || this.isInternalUser()) return Promise.resolve();
      let cache = this.dataStore.get('CHAT_CACHE');
      if (!cache) cache = await ApiConnector.findOne(Table.AttendToChatCache, { where: { user } });
      if (!cache) return Promise.resolve();
      this.dataStore.set('CHAT_CACHE', cache);
      cache.set('lastMessageSeenTime', new Date());
      return cache.save();
    });
  }

  async fetchFeedbackQuestions(condition: any): Promise<any> {
    return this.apiWall((): Promise<any> => ApiConnector.find(Table.FeedbackQuestion, {
      where: { ...condition },
      ascending: 'rank',
    }));
  }

  async fetchConsultationSessions(payload: RequestQueryPayload<Table.ConsultationSession>): Promise<Array<any>> {
    return this.apiWall((): Promise<any> => ApiConnector.find(Table.ConsultationSession, payload));
  }

  async findConsultationSession(concernClass: any, project: string[] = []): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.findOne(Table.ConsultationSession, {
      where: { username: this.getActingUser().get('username'), PrivateMainConcernClass: concernClass, archive: false },
      project,
      descending: 'createdAt',
    }));
  }

  async getLanguageStringsById(id: string[] = []): Promise<any> {
    return this.apiWall((): Promise<any> => ApiConnector.find(Table.LanguageString, { where: { objectId: id } }));
  }

  async triggerTree(stateId: any): Promise<any> {
    const username = this.getActingUser().get('username');
    return this.apiWall((): Promise<any> => this.cloudRun('switchUserToState', { stateId, username }));
  }

  fetchDietService(): Promise<any> {
    return this.apiWall((): Promise<any> => ApiConnector.findOne(Table.Service,
      { where: { type: 'DIET_CONSULTATION' }, descending: 'amount' }));
  }

  get currentWebSiteLanguage(): string {
    let currentLanguage: ValueOf<typeof this.appConfig.Shared.Languages> = this.appConfig.Shared.Languages.EN;
    Object.keys(this.appConfig.Shared.Languages)
      .map((key: string): string => this.appConfig.Shared.Languages[key])
      .forEach((language: ValueOf<typeof this.appConfig.Shared.Languages>): void => {
        if (!this.windowRef.nativeWindow.location.pathname.startsWith(`/${language}`)) return;
        currentLanguage = language;
      });
    return currentLanguage;
  }

  async getUserBlogs(payload: RequestQueryPayload<Table.UserBlog> = { where: {} }): Promise<any> {
    return this.apiWall((): Promise<any> => {
      const fetchFaq = withCache(
        'getUserBlogs',
        (payload) => ApiConnector.find(Table.UserBlog, payload),
        {},
      );
      return fetchFaq(payload);
    });
  }

  async executeCloudFunction(functionName: string, payload: object = {}): Promise<any> {
    return this.apiWall((): Promise<any> => this.cloudRun(functionName, payload as Record<string, unknown>));
  }

  async getInstantCheckupConcernDetectionCount(type: string[], limit?: number): Promise<any> {
    return this.apiWall((): Promise<any> => this.cloudRun('getInstantCheckupConcernDetectionCount', { type, limit }));
  }

  async fetchFollowUpReportById(objectId: string, project: Array<ParseKeys<Table.FollowUpReport>> = []): Promise<any> {
    return this.apiWall((): Promise<any> => ApiConnector.findOne(Table.FollowUpReport, {
      where: { objectId },
      include: ['treatmentProgress.concernLanguageString' as 'treatmentProgress', 'followUp.followUpCount' as 'followUp'],
      project,
      option: { context: { translate: true } } as Parse.FullOptions,
    }));
  }

  async fetchFollowUpReports(payload: RequestQueryPayload<Table.FollowUpReport>): Promise<any> {
    return this.apiWall((): Promise<any> => ApiConnector.find(Table.FollowUpReport, payload));
  }

  async countFollowUpReports(payload: RequestQueryPayload<Table.FollowUpReport>): Promise<any> {
    return this.apiWall((): Promise<any> => ApiConnector.count(Table.FollowUpReport, payload));
  }

  async findRecentFollowUp(where: Record<string, unknown> = {}, include: Array<any> = []): Promise<any> {
    return this.apiWall((): Promise<any> => ApiConnector.findOne(Table.FollowUp, {
      where: { user: this.getActingUser(), ...where },
      include,
      descending: 'followUpCount',
    }));
  }

  async fetchNotification(payload: RequestQueryPayload<Table.Notification>): Promise<Array<any>> {
    return this.apiWall((): Promise<any> => ApiConnector.find(Table.Notification, payload));
  }

  async fetchFeedback(payload: RequestQueryPayload<Table.Feedback>): Promise<any> {
    return this.apiWall((): Promise<any> => {
      const fetchFaq = withCache(
        'fetchFeedback',
        (payload) => ApiConnector.find(Table.Feedback, payload),
      );
      return fetchFaq(payload);
    });
  }

  async fetchLatestActiveFollowup(): Promise<any> {
    return this.apiWall((): Promise<any> => ApiConnector.findOne(
      Table.FollowUp,
      {
        where: {
          user: this.getActingUser(),
          State: [this.appConfig.Shared.Followup.State.PENDING, this.appConfig.Shared.Followup.State.SKIP],
        },
        descending: 'nextFollowUpDate',
      }));
  }

  async shouldShowFollowupBanner(): Promise<boolean> {
    const followup = await this.fetchLatestActiveFollowup();
    if (!followup
      || followup.get('followUpCount') <= 1
      || ![this.appConfig.Shared.Followup.State.PENDING,
        this.appConfig.Shared.Followup.State.SKIP].includes(followup.get('State'))) return false;
    const followupDate = this.timeService.resetTime(new Date(followup.get('nextFollowUpDate'))).getTime();
    const todayPlus2 = this.timeService.resetTime(this.timeService.addDays(new Date(), 2)).getTime();
    const today = this.timeService.resetTime(new Date()).getTime();
    return followupDate <= today || todayPlus2 >= followupDate;
  }

  async requestFollowUp(context: Record<string, unknown>): Promise<any> {
    return this.apiWall((): Promise<any> => this.switchUserToState('v4_followup:', context));
  }

  async optForRegimenDoctorCall(regimenId: string, opted: boolean): Promise<any> {
    return this.apiWall((): Promise<any> => this.cloudRun('optForRegimenDoctorCall', { regimenId, opted }));
  }

  async findDoctors(): Promise<any[]> {
    const where = { type: this.appConfig.Shared.User.Type.DOCTOR, inactive: false };
    return this.apiWall((): Promise<any> => ApiConnector.find(Table.User, { where }));
  }

  async findOrCreateConsultationSession(concernClass: string, select: string[] = []): Promise<any> {
    let session = await this.findConsultationSession(concernClass, select);
    if (session) return session;
    session = new Table.ConsultationSession();
    session.set('PrivateMainConcernClass', concernClass);
    session.set('username', this.getActingUser().get('username'));
    session.set('archive', false);
    await session.save();
    return session;
  }

  async findAllActiveCoupons(): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.find(Table.Coupon,
      { where: { active: true, showInPaymentPage: true } }));
  }

  async fetchFeeds(): Promise<any> {
    const storageKey = 'explorePosts';
    const storedData = localStorage.getItem(storageKey);
    if (storedData) {
      return Promise.resolve(JSON.parse(storedData));
    }
    const response = await this.apiWall(async (): Promise<any> => this.cloudRun('getPersonalisedExplorePosts'),
    );

    localStorage.setItem(storageKey, JSON.stringify(response));

    return response;
  }

  async fetchExplorePostById(id: string): Promise<any> {
    return this.apiWall((): Promise<any> => ApiConnector.findOne(Table.Feed, { where: { objectId: id } }));
  }

  createFileUploadUrl(param: Record<string, unknown>): Promise<any> {
    return this.apiWall((): Promise<any> => this.cloudRun('createFileUploadUrl', param));
  }

  async uploadFile({ file, bucket, username, source }: { file: any, bucket: string, username?: string, source?: string })
    : Promise<{ publicURL: string; signedGetURL: string; }> {
    const extension: string = file.name.split('.').pop();
    const name: string = `${uuid()}.${extension}`;
    this.broadcast.broadcast('UPLOADING_PROGRESS', { show: true, percentageLoaded: 0 });
    const payload = await this.createFileUploadUrl({ name, bucket, username, source, extension });
    const formData = new FormData();
    Object.keys(payload.fields).forEach((field: string): void => formData.append(field, payload.fields[field]));
    formData.append('file', file);
    const headers = new HttpHeaders();
    await new Promise((resolve: Function): void => {
      const httpRequest = this.httpClientService.post(payload.url, formData, { headers,
        responseType: 'text',
        observe: 'events',
        reportProgress: true });
      const subscription = httpRequest.subscribe((event: any): void => {
        if (event.type === 0) this.broadcast.broadcast('UPLOADING_PROGRESS', { show: true, percentageLoaded: 0, subscription });
        if (event.type === 1) {
          this.broadcast.broadcast('UPLOADING_PROGRESS',
            { show: event.total !== event.loaded,
              percentageLoaded: Math.floor((event.loaded / event.total) * 100),
              subscription });
        }
        if (event.type === 4) {
          resolve(event);
          subscription.unsubscribe();
        }
      });
    });
    return {
      publicURL: payload.publicURL,
      signedGetURL: payload.getSignedURL,
    };
  }

  async fetchAddressById(id: string): Promise<Entity['AddressBook'] | null> {
    return this.apiWall(async (): Promise<Entity['AddressBook'] | null> => {
      const res: AxiosResponse<FetchAddressBooksResponse> = await this.serverApi.fetchAddressBooks(
        this.getSessionToken(),
        0,
        100,
      );
      return res?.data?.addressBooks?.find((address: Entity['AddressBook']): boolean => address.objectId === id) ?? null;
    });
  }

  async fetchAllAddress(_project?: Array<keyof Entity['AddressBook']>): Promise<Array<Entity['AddressBook']>> {
    return this.apiWall(async (): Promise<Array<Entity['AddressBook']>> => {
      const res: AxiosResponse<FetchAddressBooksResponse> = await this.serverApi.fetchAddressBooks(
        this.getSessionToken(),
        0,
        100,
      );
      return res?.data?.addressBooks ?? [];
    });
  }

  async fetchDefaultAddress(_user: unknown): Promise<Entity['AddressBook'] | null> {
    return this.apiWall(async (): Promise<Entity['AddressBook'] | null> => {
      const fetchDefaultAddressCached = withCache(
        'fetchDefaultAddress',
        async (): Promise<Entity['AddressBook'] | null> => {
          const res: AxiosResponse<FetchAddressBooksResponse> = await this.serverApi.fetchAddressBooks(
            this.getSessionToken(),
            0,
            100,
          );
          const list: Array<Entity['AddressBook']> = res?.data?.addressBooks ?? [];
          return list.find((address: Entity['AddressBook']): boolean => !!address.default) ?? null;
        },
      );
      return fetchDefaultAddressCached({ user: _user });
    });
  }

  async fetchAddressTags(tag: string): Promise<Array<any>> {
    return this.apiWall(async (): Promise<any> => {
      const languageTags = await ApiConnector.find(Table.LanguageStringTag, { where: { name: tag } });
      return ApiConnector.find(Table.LanguageString, { where: { tags: languageTags } });
    });
  }

  async createAddressBook(address: CreateAddressBookRequest): Promise<Entity['AddressBook'] | null> {
    return this.apiWall(async (): Promise<Entity['AddressBook'] | null> => {
      const res: AxiosResponse<CreateAddressBookResponse> = await this.serverApi.createAddressBook(
        address,
        this.getSessionToken(),
      );
      return res?.data?.addressBook ?? null;
    });
  }

  async updateAddressBook(id: string, address: UpdateAddressBookRequest): Promise<Entity['AddressBook'] | null> {
    return this.apiWall(async (): Promise<Entity['AddressBook'] | null> => {
      const res: AxiosResponse<UpdateAddressBookResponse> = await this.serverApi.updateAddressBook(
        id,
        address,
        this.getSessionToken(),
      );
      return res?.data?.addressBook ?? null;
    });
  }

  async changeDefaultAddress(id: string): Promise<Entity['AddressBook'] | null> {
    return this.apiWall(async (): Promise<Entity['AddressBook'] | null> => {
      const res: AxiosResponse<ChangeDefaultResponse> = await this.serverApi.changeDefault(
        id,
        this.getSessionToken(),
      );
      return res?.data?.addressBook ?? null;
    });
  }

  async deleteAddressBook(id: string): Promise<DeleteAddressBookResponse> {
    return this.apiWall(async (): Promise<DeleteAddressBookResponse> => {
      const res: AxiosResponse<DeleteAddressBookResponse> = await this.serverApi.deleteAddressBook(
        id,
        this.getSessionToken(),
      );
      return res?.data ?? {};
    });
  }

  applyGiftCard(code: string): Promise<any> {
    return this.apiWall((): Promise<any> => this.cloudRun('applyGiftCard', { code }));
  }

  getDetailedLocationFromCoordinates(data: { latitude: string | number; longitude: string | number } | Record<string, string>): Promise<ReverseGeocodeData | undefined> {
    const coords = data as { latitude: string | number; longitude: string | number };
    return this.apiWall(async (): Promise<ReverseGeocodeData | undefined> => {
      const res: AxiosResponse<ReverseGeocodeResponse> = await this.serverApi.reverseGeocode(
        String(coords.latitude),
        String(coords.longitude),
        this.getSessionToken(),
      );
      return res?.data?.results as ReverseGeocodeData | undefined;
    });
  }

  findFollowupById(id: string, context: Record<string, unknown>): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.findOne(Table.FollowUp, {
      where: { objectId: id },
      option: { context } as Parse.FullOptions,
    }));
  }

  async createFollowUp(): Promise<any> {
    if (this.isInternalUser()) return {};
    return this.apiWall((): Promise<any> => this.cloudRun('createFollowUp', {}));
  }

  async trackInFirebaseAndBranch(eventName: string, eventData?: { [key: string]: unknown }): Promise<any> {
    const os = await this.getAppBridgeService().requestOSInformation();
    if (os === 'iOS') {
      return Promise.resolve();
    }
    return this.apiWall((): Promise<any> => this.cloudRun('trackEventInBranchAndFirebase', { eventName, eventData }));
  }

  fetchSupportCategory(payload: RequestQueryPayload<Table.SupportCategory> = { where: {} },
    context: Record<string, any> = {}): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.find(Table.SupportCategory, {
      ...payload,
      option: { context } as Parse.FullOptions,
    }));
  }

  fetchSupportQuestions(payload: RequestQueryPayload<Table.SupportQuestion>, context: Record<string, any> = {}): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.find(Table.SupportQuestion, {
      include: ['questionLanguageString', 'responseHtmlLanguageString', 'responseTitleLanguageString'],
      option: { context: { translate: true, response: true, ...context } } as Parse.FullOptions,
      ...payload,
    }));
  }

  fetchSupportQuestionById(payload: RequestQueryPayload<Table.SupportQuestion>, context?: Record<string, any>): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.findOne(Table.SupportQuestion, {
      option: { context: { response: true, translate: true, ...context } } as Parse.FullOptions,
      ...payload,
    }));
  }

  fetchSupportTickets(payload: RequestQueryPayload<Table.SupportTicket>): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.find(Table.SupportTicket,
      {
        ...payload,
        include: ['supportQuestion', 'supportQuestion.questionLanguageString' as 'supportQuestion'],
        option: { context: { translate: true } } as Parse.FullOptions,
        descending: 'createdAt',
      }));
  }

  findSupportTicketById(id: string, context: Record<string, unknown>): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.findOne(Table.SupportTicket, {
      where: { objectId: id },
      option: { context } as Parse.FullOptions,
    }));
  }

  fetchSupportChat(payload: RequestQueryPayload<Table.SupportChat>): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.find(Table.SupportChat,
      {
        ...payload,
        option: { context: { translate: true, response: true } } as Parse.FullOptions,
      }));
  }

  fetchSupportTicketCount(user: any): Promise<number> {
    return this.apiWall(async (): Promise<any> => ApiConnector.count(Table.SupportTicket,
      { where: { user } }));
  }

  getNextQuizQuestion(): Promise<QuizNextQuestionResponse['data']> {
    return this.apiWall(async (): Promise<QuizNextQuestionResponse['data']> => {
      const sessionToken = this.getSessionToken();
      const result = await this.serverApi.getNextQuizQuestion(sessionToken);
      return result.data.data;
    });
  }

  storeQuizQuestionAnswer(questionId: string, answerIndex: number): Promise<QuizSubmitAnswerResponse['data']> {
    return this.apiWall(async (): Promise<QuizSubmitAnswerResponse['data']> => {
      const sessionToken = this.getSessionToken();
      const result = await this.serverApi.submitQuizAnswer(sessionToken, {
        questionId,
        selectedAnswerIndex: answerIndex,
      });
      return result.data.data;
    });
  }

  fetchUserAnswers(user: Table.User): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.find(Table.QuizAnswers,
      {
        where: { user },
        limit: 1000,
        include: ['questionId'],
        descending: 'createdAt',
      }));
  }

  async createTicket(supportQuestionId: string, title?: string, extraData?: Record<string, any>): Promise<any> {
    return this.apiWall((): Promise<any> => this.cloudRun('findOrCreateSupportTicket',
      { supportQuestionId, title, extraData }));
  }

  async updateUserLastActiveDetails(supportTicketId: string): Promise<any> {
    return this.apiWall((): Promise<any> => this.cloudRun('updateUserLastActiveDetails', { supportTicketId }));
  }

  fetchReminderById(id: string): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.findOne(Table.Reminder, { where: { objectId: id } }));
  }

  async updateSupportTicketFeedback(
    supportTicketId: string, feedback: boolean, userQuery?: string, noCallNeeded?: boolean):
    Promise<{ message: string, ticketStatusChanged: boolean }> {
    return this.apiWall((): Promise<any> => this.cloudRun(
      'updateSupportTicketFeedback',
      { supportTicketId, feedback, userQuery, noCallNeeded },
      { context: { skipMessage: true } }));
  }

  async findUnReadNotifications(): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.count(Table.Notification, {
      where: {
        user: this.getActingUser(),
        viewed: false,
        active: true,
      },
    }));
  }

  async findLastDeliveredOrder(where: Record<string, unknown> = {}): Promise<any> {
    return this.apiWall(async (): Promise<any> => {
      const findLastDeliveredOrder = withCache(
        'findLastDeliveredOrder',
        (params) => ApiConnector.findOne(Table.Order, params),
      );
      return findLastDeliveredOrder({
        where: { user: this.getActingUser(), ...where },
        include: ['regimen'],
        descending: 'deliveredOn',
      });
    });
  }

  async updateActingUser({ userId, username }: { userId: any; username: any }): Promise<void> {
    if (!this.isInternalUser()) {
      return;
    }
    const payload: RequestQueryPayload<Table.User> = { where: {} };
    if (userId) {
      payload.where.objectId = userId;
    } else if (username) {
      payload.where.username = username;
    } else {
      return;
    }
    this.actingUser = await ApiConnector.findOne(Table.User, payload);
    if (this.actingUser && !this.actingUser.isPaid) {
      this.actingUser.isPaid = this.isPaid.call(this.actingUser, this);
    }
  }

  getActingUser(): any {
    if (this.actingUser) {
      return this.actingUser;
    }
    return this.getCurrentUser();
  }

  updateRegimenPrice(payload: any): Promise<any> {
    return this.trackRegimenMutation(
      this.apiWall((): Promise<boolean> => this.cloudRun('updateRegimenPriceAndAddExtraProducts', payload)),
    );
  }

  async fetchExperimentByKey(key: string): Promise<any> {
    return this.apiWall(async (): Promise<any> => {
      const fetchExperimentByKey = withCache(
        'fetchExperimentByKey',
        (params) => ApiConnector.findOne(Table.Experiment, params),
      );
      return fetchExperimentByKey({ where: { key, enable: true } });
    });
  }

  getCart(): Promise<any> {
    return this.apiWall((): Promise<any> => {
      const getCart = withCache(
        'getCart',
        (params) => this.cloudRun('getCart', params),
      );
      return getCart({});
    });
  }

  addProductToCart(payload: any): Promise<any> {
    let cart: any;
    if (payload.productId) {
      cart = this.apiWall((): Promise<boolean> => {
        resetCacheByName('getCart');
        return this.cloudRun('addProductToCart', payload);
      });
    }
    return cart;
  }
  async addProductToNewCart(payload: any): Promise<any> {
    try {
      const sessionToken = this.getSessionToken();
      const params = { ...payload, userId: this.user.id };
      if (sessionToken) {
        resetCacheByName('getCart');
        const response = await this.serverApi.addProductToCart(params, sessionToken);
        return response?.data?.data ?? [];
      } return [];
    } catch (error) {
      console.error('Error adding product to cart:', error);
      return [];
    }
  }

  async getRefillProducts(): Promise<any[]> {
    const sessionToken = this.getSessionToken();
    if (!sessionToken) return [];
    try {
      const response = await this.serverApi.getRefillProducts(sessionToken);
      return response?.data?.data?.refillProducts ?? [];
    } catch (error) {
      console.error('Error fetching refill products:', error);
      return [];
    }
  }

  removeProductFromCart(payload: any): Promise<any> {
    return this.apiWall((): Promise<boolean> => {
      resetCacheByName('getCart');
      return this.cloudRun('removeProductFromCart', payload);
    });
  }

  trackVoiceNotesAndScrollEvents(payload: any): Promise<any> {
    return this.apiWall((): Promise<boolean> => this.cloudRun('trackVoiceNotesAndScrollEvents', payload));
  }

  async findProductsByText(text: string): Promise<any[]> {
    const username = this.getActingUser().get('username');
    return this.apiWall((): Promise<boolean> => this.cloudRun('findProductsByText', { username, text }));
  }

  async advancedSearchProductsByText(text: string): Promise<any[]> {
    const username = this.getActingUser().get('username');
    return this.apiWall((): Promise<boolean> => this.cloudRun('advancedSearchProductsByText', { username, text }));
  }

  async findProducts(where: any, project: Array<ParseKeys<Table.Catalog>> = [], skipInventoryStatusCheck: boolean = false): Promise<Array<Table.Catalog>> {
    return this.apiWall((): Promise<any[]> => {
      const findProducts = withCache(
        'findProducts',
        (params) => ApiConnector.find(Table.Catalog, params),
      );
      const whereClause = skipInventoryStatusCheck ? { ...where } : { ...where, inventoryStatus: this.appConfig.Shared.Inventory.Type.AVAILABLE };
      return findProducts({
        where: whereClause,
        project,
      });
    });
  }

  async findQuestionById(id: Array<string>): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.find(Table.QuizQuestion, {
      where: { objectId: id },
      include: ['answer', 'optionsArray', 'questionLanguageString', 'wrongAnswer'],
      option: { context: { translate: true } } as Parse.FullOptions,
    }));
  }

  findCatalogWithAlternateProduct(productId: any): Promise<any> {
    return this.apiWall(async (): Promise<any> => {
      const findCatalogWithAlternateProduct = withCache(
        'findCatalogWithAlternateProduct',
        (params) => ApiConnector.findOne(Table.Catalog, params),
      );
      return findCatalogWithAlternateProduct({ where: { objectId: productId }, include: ['alternateProduct'] });
    });
  }

  replaceDiscontinuedProduct(oldProductId: string, newProductId: string): any {
    const username = this.getActingUser().get('username');
    return this.apiWall((): Promise<boolean> => this.cloudRun('replaceDiscontinuedProduct',
      { oldProductId, newProductId, username }));
  }
  // Refer and Earn
  referNewUserWithNumber(referredUserName: string, referredMobileNumber: string): Promise<any> {
    return this.apiWall((): Promise<boolean> => this.cloudRun('referNewUserWithNumber',
      { referredMobileNumber, referredUserName }));
  }
  fetchLatestActiveSupportTickets(payload: RequestQueryPayload<Table.SupportTicket>): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.findOne(Table.SupportTicket,
      {
        ...payload,
        include: ['supportQuestion', 'supportQuestion.questionLanguageString' as 'supportQuestion'],
        option: { context: { translate: true } } as Parse.FullOptions,
        descending: 'createdAt',
      }));
  }

  fetchImageFromMediaLink(uniqueId: string): Promise<any> {
    return this.apiWall((): Promise<any> => ApiConnector.find(Table.MediaLink,
      {
        where: { uniqueId },
        project: ['link'],
      },
    ));
  }

  updateRegimenInfo(regimenId: any, variantId: string): Promise<any> {
    return this.trackRegimenMutation(
      this.apiWall((): Promise<boolean> => this.cloudRun('changeRegimenVariant', { regimenId, variantId })),
    );
  }

  fetchProgressiveProduct(): Promise<any> {
    const user = this.getActingUser();
    return this.apiWall((): Promise<any> => ApiConnector.findOne(Table.ProgressiveTreatment,
      { where: { user, active: true, completed: false }, include: ['product'] }));
  }


  /*
    create an interface with type of value for each concerns
  */
  getSkinReportInfo(mainConcernDetectedByAI?: string, concernsWithCount?: ConcernsWithCount): Promise<any> {
    const payload = {
      mainConcernDetectedByAI,
      concernsWithCount,
    };
    return this.apiWall((): Promise<boolean> => this.cloudRun('getUserSkinReportParameters', payload));
  }

  createPendingCall(payload: { type: string, message: string}): Promise<any> {
    return this.apiWall((): Promise<boolean> => this.cloudRun('createPendingCall', payload));
  }

  trackEventInCleverTap(eventName: string, username: string, eventData: any = {}, channels?: string[]): Promise<any> {
    return this.apiWall(async (): Promise<void> => {
      await (this.eventServerApi as any).trackEvent({
        eventName,
        eventData,
        channels: channels as any,
        username,
      }, this.getSessionToken())
    });
  }

  getClassBasedOnLanguagePreference(): string {
    const user = this.getActingUser();
    const userLanguagePreference = user?.get('languagePreference') || this.appConfig.Shared.Languages.EN;
    return userLanguagePreference !== 'hi' ? 'tw-text-100' : 'tw-text-300';
  }


  async findLastVerifiedConsultation(orderId: string): Promise<any> {
    const order = new Table.Order();
    order.id = orderId;
    return this.apiWall(async (): Promise<any> => ApiConnector.findOne(Table.OrderConsultationLog, {
      where: { order, stage: 'DR_VERIFIED' },
      descending: 'createdAt',
    }));
  }

  async getOrderId(regimenId: string): Promise<any> {
    /* eslint-disable */
    return this.apiWall(async (): Promise<any> => ApiConnector.find(Table.Order, {
      where: { regimenId: regimenId },
      project: ['objectId'] }));
  }

  async getPastRegimens(user: any, fromDate?: Date, regimenClass?: string): Promise<any> {
    return this.apiWall(async (): Promise<any> => {
      const where: any = {
        forUser: user,
        ...(fromDate ? { createdAt: { $gte: fromDate }, class: regimenClass } : { updateVersion: 0 })
      };
      return ApiConnector.find(Table.RegimenHistory, {
        where,
      });
    });
  }

  getRegimenVariantSelection(): boolean {
    return this.isVariantAlreadySelected;
  }

  setRegimenVariantSelection(): void {
    this.isVariantAlreadySelected = true;
  }

  async logPrivacyPolicyDate(): Promise<void> {
    const user = this.getActingUser();
    if (user) {
      const today = new Date();
      const userPolicyAcceptedDates = user?.get('termsOfServiceAcceptedDates') || [];
      if (userPolicyAcceptedDates.includes(today)) return;

      const dateArray = [today];
      user.set('termsOfServiceAcceptedDates', [...userPolicyAcceptedDates, ...dateArray]);
      try {
        await user.save();
      } catch (error) {
        console.error('Error saving user:', error);
      }
    }
  }

  async findAllSectionWiseProductsServerAPI(): Promise<Record<string, Array<Models.Catalog>>> {
    const sessionToken = this.getSessionToken();

    if (!sessionToken) {
      return {};
    }
    const findAllSectionWiseProducts = withCache(
      'findAllSectionWiseProductsServerAPI',
      (params) => this.serverApi.findAllSectionWiseProducts(params),
    );
    const response: any = await findAllSectionWiseProducts(sessionToken);
    return response.data?.result ?? {};
  }

  async findSectionProductsServerAPI(section: string, useCache: boolean): Promise<Array<Models.Catalog>> {
    const cacheKey = this.tagCacheTableMapping[section];
    const sessionToken = this.getSessionToken();

    if (!sessionToken || !section) {
      return [];
    }

    const cb = async (): ServerAPIResponse['findSectionProducts'] => {
      return (await this.serverApi.findSectionProducts(section, sessionToken))?.data;
    }

    if (useCache) {
      if (!cacheKey) return [];
      const cachedFindAllProducts = this.cachedFnWrapper(cb, cacheKey);
      const response = await cachedFindAllProducts();
      return response?.result ?? [];
    }

    const response = await this.executeCallbackAndCacheResult(cb, cacheKey);
    return response?.result ?? [];
  }

  async createPendingCallAndSaveNote(supportTicket: any): Promise<any> {
    try {
      const sessionToken = this.getSessionToken();

      if (!sessionToken) {
        return [];
      }

      const response = await this.apiWall(
        (): Promise<any> => this.serverApi.createUserRequestedCall(supportTicket, sessionToken));
      return response?.data ?? [];
    } catch (error) {
      console.error('Error in creating pending call:', error);
      return [];
    }
 }
  async getUserCheckupData(): Promise<ServerAPIModels['userCheckupData']> {
    const currentUserSessionToken = this.getSessionToken();
    const userId = this.getActingUser().id;
    const response: ServerAPIResponse['userCheckupDataResponse'] = (await this.serverApi.getUserCheckupData(userId, currentUserSessionToken))?.data;
    return response?.data ?? {};
  }

  async getExpectedDeliveryDateByOrderId(orderId: string): Promise<any> {
    const currentUserSessionToken = this.getSessionToken();
    const response: any = await this.serverApi.getExpectedDeliveryDateByOrderId(
      orderId, currentUserSessionToken);
    return response?.data ?? {};
  }

  async getExpectedDeliveryDateByDestinationPinCode(pincode: string): Promise<any> {
    const currentUserSessionToken = this.getSessionToken();
    const response: any = await this.serverApi.getExpectedDeliveryDateByDestinationPinCode(
      pincode, currentUserSessionToken);
    return response?.data ?? {};
  }

  async translateBotQuestion(params: TranslateLastBotQuestionData): Promise<void> {
    const currentUserSessionToken = this.getSessionToken();
    const user = this.getActingUser();

    return await this.apiWall((): Promise<AxiosResponse> =>
      this.serverApi
        .translateChatQuestion(
          {
            questionId: params.questionId,
            userId: user?.id,
            toLanguage: params.toLanguage,
            regimenClass: params.regimenClass,
          },
          currentUserSessionToken
        )
        .then((response: any): any => {
          return response;
        })
        .catch((error: any): any => {
          throw error;
        })
    );
  }

  async getAllPendingCallsForType(pendingCallType: string): Promise<any> {
    const userId = this.getActingUser().id;
    const currentUserSessionToken = this.getSessionToken();
    const response = await this.serverApi.getUserPendingCallsByType(
      userId,
      pendingCallType,
      currentUserSessionToken,
    );
    return response?.data?.data ?? [];
  }

  async createUnpaidUserDoctorCall(regimenObjId: string): Promise<any> {
    const currentUserSessionToken = this.getSessionToken();
    const response = await this.serverApi.createUnpaidUserDoctorCall(
      {regimenId: regimenObjId},
      currentUserSessionToken,
    );
    return response ?? {};
  }

  async getListOfChangedRegimenProduct(regimenId: string, actionQuestionUniqueId: string ): Promise<ServerAPIModels['actionLog'][]> {
    const userId = this.getActingUser().id;
    const currentUserSessionToken = this.getSessionToken();

    const response: ServerAPIResponse['userListOfChangedRegimenProductResponse'] = (await this.serverApi.getListOfChangedRegimenProduct(
      actionQuestionUniqueId, userId, regimenId, currentUserSessionToken))?.data;
    return response?.data ?? [];
  }

  /**
   * Persists the free-text feedback message a user typed in the report feedback
   * bottom-sheet, keyed by the question they answered "No" to.
   * `key` is one of isReportSatisfied | isVoiceNoteUnderstandable | doubtsCleared.
   */
  async saveFollowUpReportFeedbackMessage(followUpReportId: string, key: FollowUpReportFeedbackQuestion, message: string):
  Promise<FollowUpReportFeedbackMessageMap> {
    const response = await this.serverApi.submitFollowUpReportFeedbackMessage(
      this.getSessionToken(),
      followUpReportId,
      { key, message },
    );
    return response?.data?.data?.feedbackMessages ?? {};
  }

  async updateFollowUpReportUserFields(followUpReportId: string, fields: {
    feedback?: Record<string, unknown>;
    consentFormAsk?: boolean;
    read?: boolean;
  }): Promise<void> {
    await this.serverApi.updateFollowUpReportUserFields(
      this.getSessionToken(),
      followUpReportId,
      fields,
    );
  }

  /**
   * Returns the regimen product changes on a follow-up report — products added, removed, and whose
   * instructions changed — as full catalogs, used by the "Product Update in Your Treatment" and
   * "Products Added" sections.
   */
  async getFollowUpReportTreatmentUpdates(followUpReportId: string): Promise<FollowUpReportTreatmentUpdates> {
    const response = await this.serverApi.getFollowUpReportTreatmentUpdates(this.getSessionToken(), followUpReportId);
    const updates = response?.data?.data;
    return {
      addedProducts: updates?.addedProducts ?? [],
      removedProducts: updates?.removedProducts ?? [],
      instructionChanged: updates?.instructionChanged ?? [],
    };
  }


  async fetchLatestOrderConsultationLog(orderId: string): Promise<any> {
    const currentUserSessionToken = this.getSessionToken();
    const response = await this.serverApi.getLatestOrderConsultationLog(orderId, currentUserSessionToken);
    return response?.data?.data ?? {};
  }

  async fetchNewCart(userId: string): ServerAPIResponse['userCart'] {
    const currentUserSessionToken = this.getSessionToken();
    return (await this.serverApi.getCart(userId, currentUserSessionToken))?.data;
  }

  async findAllPendingTicketWithAwaitingResponse(): Promise<any> {
      const userId = this.getActingUser().id;
      const currentUserSessionToken = this.getSessionToken();
      const res = await this.serverApi.getAwaitingUserResponseTickets(userId, currentUserSessionToken);
      return res.data.result;
  }

  async markFollowUpReadyStatus(followUpId: string): Promise<any> {
    const currentUserSessionToken = this.getSessionToken();
    const res = await this.serverApi.markReady(currentUserSessionToken, followUpId);
    return res.data.data;
  }

  async findTreatmentSession(): Promise<any> {
    const currentUserSessionToken = this.getSessionToken();
    const response = await this.serverApi.findTreatmentSession(currentUserSessionToken);
    return response?.data ?? {};
  }

  async fetchTreatmentChat(payload: RequestQueryPayload<Table.TreatmentSessionChat>): Promise<any> {
    return this.apiWall(async (): Promise<any> => ApiConnector.find(Table.TreatmentSessionChat, payload));
  }

  async createOrUpdateViewedProduct(productId: string): Promise<any> {
    const currentUserSessionToken = this.getSessionToken();
    const response = await this.serverApi.createOrUpdateViewedProduct(productId, currentUserSessionToken);
    return response?.data?.data ?? {};
  }

  async fetchRecentlyViewedProducts(days: number, limit?: number): Promise<any> {
    const currentUserSessionToken = this.getSessionToken();
    const response = await this.serverApi.getRecentlyViewedProducts(days, currentUserSessionToken, limit);
    return response?.data?.data ?? {};
  }

  async showNotificationPermissionPopup(): Promise<ShowNotificationPermissionPopupData> {
    const installationId = this.resolveInstallationId();
    const currentUserSessionToken = this.getSessionToken();
    try {
      const res = await this.serverApi.showNotificationPermissionPopup(installationId, currentUserSessionToken);
      return res?.data?.data;
    } catch (error) {
      console.error('Error checking notification permission:', error);
      return { showNotificationPopUp : false };
    }
  }

  async setNotificationPopupResponse(popupResponse: boolean): Promise<any> {
    const installationId = this.resolveInstallationId();
    const currentUserSessionToken = this.getSessionToken();
    const reqBody = {
      installationId, popupResponse
    }
    try {
      const res = await this.serverApi.updateNotificationPermissionDate(reqBody, currentUserSessionToken);
      return res?.data;
    } catch (error) {
      console.error('Error updating notification permission response:', error);
      return null;
    }
  }

  async getActiveProgressiveTreatmentOfUser(): Promise<any> {
    const userId = this.getActingUser().id;
    const currentUserSessionToken = this.getSessionToken();
    const res = await this.serverApi.getActiveProgressiveTreatmentOfUser(userId, currentUserSessionToken);
    return res?.data;
  }

  async getTestimonials(): Promise<TestimonialResponseItem[]> {
    try {
      const currentUserSessionToken = this.getSessionToken();
      const res = await this.serverApi.getTopTestimonials(currentUserSessionToken);
      return res?.data?.testimonials ?? [];
    } catch {
      return [];
    }
  }

  // Caching decision (explicit-cache-decision): not cached — the twin rule set can change between views, so every report open re-evaluates.
  async getTwinTestimonial(regimenClass: RegimenClass): Promise<TestimonialResponseItem | null> {
    try {
      const currentUserSessionToken = this.getSessionToken();
      const res = await this.serverApi.getTwinTestimonial(regimenClass, currentUserSessionToken);
      return res?.data?.testimonial ?? null;
    } catch {
      return null;
    }
  }

  async showOnHoldPopup(): Promise<ShowOnHoldPopUpResponseData> {
    const currentUserSessionToken = this.getSessionToken();
    const res = await this.serverApi.showOnHoldPopUp(currentUserSessionToken);
    return res?.data.data;
  }

  async onHoldPopupUserAction(regimenObjectId: string, ticketId: string, userResponse: OnHoldUserResponseRequestBodyUserResponseEnum): Promise<any> {
    const currentUserSessionToken = this.getSessionToken();
    const res = await this.serverApi.handleUserResponseOnHolPopUp(currentUserSessionToken, regimenObjectId,
    { ticketId, userResponse });
    return res?.data;
  }


  async getDeliveredMargIdsByDays(lookBackDays: number): Promise<GetDeliveredMargIdsResponse> {
    const currentUserSessionToken = this.getSessionToken();
    const res = await this.serverApi.getDeliveredMargIdsByDays(lookBackDays, currentUserSessionToken);
    return res?.data;
  }

  async shouldShowRegimenExpiryBanner(): Promise<RegimenExpiryBannerResponse['data']> {
    const currentUserSessionToken = this.getSessionToken();
    const user = this.getActingUser();
    const res = await this.serverApi.shouldShowRegimenExpiryBanner(user?.id, currentUserSessionToken);
    return res?.data?.data ?? {};
  }

  async fetchCoupon(couponCode: string): Promise<GetCouponResponse['data']> {
    const currentUserSessionToken = this.getSessionToken();
    const res = await this.serverApi.getCoupon(couponCode, currentUserSessionToken);
    return res?.data?.data ?? {};
  }

  async fetchSuitableFeedsForUser(): Promise<GetSuitableFeedForUserResponse> {
    const sessionToken = this.getSessionToken();

    const fetchFeeds = withCache(
      'fetchSuitableFeedsForUser',
      () => this.serverApi.getSuitableFeedForUser(sessionToken),
      { retainForSession: true },
    );
    const response = await fetchFeeds({});
    return response?.data;
  }

  async fetchRegimenFaqPosts(regimenClass: 'FACE' | 'HAIR'): Promise<GetRegimenFaqPostsForUserResponse> {
    const user = this.getActingUser();
    const sessionToken = user?.get('sessionToken');

    const fetchPosts = withCache(
      `fetchRegimenFaqPosts_${regimenClass}`,
      () => this.serverApi.getRegimenFaqPostsForUser(regimenClass, sessionToken),
      { retainForSession: true },
    );
    const response = await fetchPosts({});
    return response?.data;
  }

  async getBranchLink(feedId: string): Promise<ObtainBranchLinkResponse> {
    const sessionToken = this.getSessionToken();

    const response = await this.serverApi.obtainBranchLink(sessionToken, feedId);
    return response?.data;
  }

  async getDeliveredProducts(): Promise<FindDeliveredProductsOfUserResponse> {
    const sessionToken = this.getSessionToken();

    const response = await this.serverApi.findDeliveredProductsOfUser(sessionToken);
    return response?.data;
  }


  async updateViewedProductStatus(productId: string, isActive: boolean): Promise<any> {
    const currentUserSessionToken = this.getSessionToken();
    const res = await this.serverApi.updateViewedProductStatus(productId, currentUserSessionToken, { isActive });
    return res?.data?.data ?? {};
  }

  async getInstructionsForUser(): Promise<GetUserInstructionDetails> {
    const user = this.getActingUser();
    const currentUserSessionToken = this.getSessionToken();
    const res = await this.serverApi.getAllProductInstructionForUser(user?.id, currentUserSessionToken);
    return res?.data ?? {};
  }

  async getRefundInfoForOrder(orderId: string) {
    const currentUserSessionToken = this.getSessionToken();
    const result = await this.serverApi.getRefundInfo(orderId, currentUserSessionToken);
    return result?.data?.refundInfo ?? {};
  }


  async checkIfUserAddressExistsInShipRocket(userPhoneDetails: CheckAddressRequest): Promise<CheckAddressResponse> {
    const sessionToken = this.getSessionToken();
    const response = await this.serverApi.checkAddressExists(userPhoneDetails, sessionToken);
    return response?.data;
  }

  async initiateShipRocketLogin(userPhoneDetails: InitiateLoginRequest): Promise<InitiateLoginResponse> {
    const sessionToken = this.getSessionToken();
    const response = await this.serverApi.initiateLogin(userPhoneDetails, sessionToken);
    return response?.data;
  }

  async verifyShipRocketLoginToken(verifyTokenRequestData: VerifyTokenRequest): Promise<VerifyTokenResponse> {
    const sessionToken = this.getSessionToken();
    const response = await this.serverApi.verifyToken(verifyTokenRequestData, sessionToken);
    return response?.data;
  }

  async getShiprocketAddresses(getAddressesRequest: GetAddressesRequest): Promise<GetAddressesResponse> {
    const sessionToken = this.getSessionToken();
    const response = await this.serverApi.getAddresses(getAddressesRequest, sessionToken);
    return response?.data;
  }

  async importShiprocketAddress(importAddressRequest: ImportAddressRequest): Promise<ImportAddressResponse> {
    const sessionToken = this.getSessionToken();
    const response = await this.serverApi.importAddress(importAddressRequest, sessionToken);
    return response?.data;
  }

  async getProductUsage(regimenId: string, date?: string): Promise<any> {
    const currentUserSessionToken = this.getSessionToken();
    const res = await this.serverApi.getProductUsage(regimenId, currentUserSessionToken, date);
    return res?.data?.data;
  }

  async updateProductUsage(
    objectId: string,
    productUsage?: Array<{ productObjectId: string; partOfDay: 'MORNING' | 'NIGHT'; isUsed: boolean }>,
    introBottomsheetShown?: boolean,
  ): Promise<ProductUsageTrackingResponseData> {
    const currentUserSessionToken = this.getSessionToken();
    const res = await this.serverApi.updateProductUsage({ objectId, productUsage, introBottomsheetShown }, currentUserSessionToken);
    return res?.data?.data;
  }

  async submitDefaultAnswersForConsultation(consultationId: Table.ConsultationSession['id']): Promise<SubmitDefaultAnswersResponse> {
    const currentUserSessionToken = this.getSessionToken();
    const res = await this.serverApi.submitDefaultAnswers(consultationId, currentUserSessionToken);
    return res?.data ?? {};
  }

  async processIsotretinoinConsent(orderId: string, isConsentGiven: boolean): Promise<any> {
    const currentUserSessionToken = this.getSessionToken();
    const res = await this.serverApi.processIsotretinoinConsent(currentUserSessionToken, { orderId, isConsentGiven });
    resetCacheByName('fetchOrders');
    resetCacheByName('getOrders');
    this.localStorage.setRegimens([], this.getActingUser());
    this.store.dispatch(fromActions.RegimenStateUpdate({ regimens: [], regimensFetched: false }));
    return res?.data ?? {};
  }

  async createIsotretinoinConsentCall(orderId: string): Promise<any> {
    const currentUserSessionToken = this.getSessionToken();
    const res = await this.serverApi.createIsotretinoinConsentCall(currentUserSessionToken, { orderId });
    return res?.data ?? {};
  }

  async getUserExtraInformation(): Promise<any> {
    const currentUserSessionToken = this.getSessionToken();
    const res = await this.serverApi.getUserExtraInformation(currentUserSessionToken);
    return res?.data?.data ?? {};
  }

  async updatePregnancyStatus(pregnancyTag?: 'PREGNANT' | 'NEW_MOM' | 'PLANNING_FOR_PREGNANCY'): Promise<any> {
    const currentUserSessionToken = this.getSessionToken();
    const res = await this.serverApi.updatePregnancyStatus(currentUserSessionToken, pregnancyTag ? { pregnancyTag } : {});
    return res?.data ?? {};
  }

  async findUserChatCache(): Promise<any> {
    const user = this.getActingUser();
    return ApiConnector.findOne(Table.AttendToChatCache, { where: { user }, project: ['label'] });
  }

  getVariantsByMainProductId(mainProductId: string): Promise<{ variants: any[] }> {
    return this.apiWall(async (): Promise<{ variants: any[] }> => {
      const sessionToken = this.getSessionToken();
      const response = await this.serverApi.getVariantsByMainProductId(mainProductId, sessionToken);
      return { variants: response?.data?.variants ?? [] };
    });
  }

  async createOutgoingCallFeedback(payload: { score: number; callLogId?: string; feedback?: string }): Promise<any> {
    const sessionToken = this.getSessionToken();
    const response = await this.serverApi.createOutgoingCallFeedback(payload, sessionToken);
    return response?.data;
  }

  async showReOrderBanner(): Promise<ReorderKitBannerResponse> {
    const user = this.getActingUser();
    const sessionToken = this.getSessionToken();
    const response = await this.serverApi.showRegimenReorderBanner(user.id, sessionToken);
    return response?.data ?? {};
  }

  async changeUserCommunicationLanguage(languageCode: string): Promise<void> {
    const user = this.getCurrentUser();
    await this.serverApi.changeUserCommunicationLanguage({ communicationLanguagePreference: languageCode }, user?.get('sessionToken'));
    user?.set('communicationLanguagePreference', languageCode);
  }

  async translateVoiceNote(orderChatId: string): Promise<void> {
    const sessionToken = this.getSessionToken();
    await this.serverApi.translateVoiceNote(orderChatId, sessionToken);
  }

  async createPaymentLink(orderId: string, isPartialPayment?: boolean): Promise<{ shortUrl: string; paymentLinkId: string; paymentObjectId: string }> {
    const sessionToken = this.getSessionToken();
    const response = await this.serverApi.createPaymentLink(orderId, sessionToken, { isPartialPayment: !!isPartialPayment });
    return response?.data?.data;
  }

  async getPersonalisedPromotions(): Promise<PersonalisedPromotion[]> {
    const fetchPromotions = withCache<PersonalisedPromotion[]>(
      'getPersonalisedPromotions',
      async (): Promise<PersonalisedPromotion[]> => {
        try {
          const sessionToken = this.getSessionToken();
          const response = await this.serverApi.getPersonalisedPromotions(sessionToken);
          return response?.data?.data?.personalisedPromotions ?? [];
        } catch {
          return [];
        }
      },
      { retainForSession: true },
    );
    return fetchPromotions(undefined);
  }

  async removeProductFromNewCart(payload: any): Promise<any> {
    try {
      const user = this.getActingUser();
      const sessionToken = user?.get('sessionToken');
      const params = { ...payload, userId: this.user.id };
      if (sessionToken) {
        resetCacheByName('getCart');
        const response = await this.serverApi.removeProductFromCart(params, sessionToken);
        return response?.data?.data ?? [];
      } return [];
    } catch (error) {
      console.error('Error removing product from cart:', error);
      return [];
    }
  }

  // No caching — every upload must be classified fresh.
  async classifyReport(imageUrl: string): Promise<{ isReport?: boolean }> {
    const sessionToken = this.getSessionToken();
    const response = await this.serverApi.classifyDocument(sessionToken, { imageUrl });
    return response?.data;
  }

  async getShiprocketAuthorisedToken(
    userPhoneDetails: GetAuthorisedTokenRequest,
  ): Promise<GetAuthorisedTokenResponse> {
    const sessionToken = this.getSessionToken();
    const response = await this.serverApi.getAuthorisedToken(userPhoneDetails, sessionToken);
    return response?.data;
  }

  async importAllShiprocketAddresses(
    importAllRequest: ImportAddressRequest,
  ): Promise<ImportAddressResponse> {
    const sessionToken = this.getSessionToken();
    const response = await this.serverApi.importAddress(importAllRequest, sessionToken);
    return response?.data;
  }

  async loginGenerateShiprocket(
    generateRequest: ShiprocketGenerateRequest,
  ): Promise<LoginGenerateShiprocketResponse> {
    const response = await this.serverApi.loginGenerateShiprocket(generateRequest);
    return response?.data;
  }

  async loginVerifyShiprocket(
    verifyRequest: ShiprocketVerifyRequest,
  ): Promise<LoginVerifyResponse> {
    const response = await this.serverApi.loginVerify(verifyRequest);
    return response?.data;
  }

  async findDoctorById(doctorId: string): Promise<DoctorProfile> {
    const sessionToken = this.getSessionToken();
    const getDoctorById = withCache(
      'findDoctorById',
      (id: string) => this.serverApi.getDoctorById(id, sessionToken),
    );
    return (await getDoctorById(doctorId))?.data?.data as DoctorProfile;
  }

  async findAllActiveDoctors(): Promise<DoctorProfile[]> {
    const sessionToken = this.getSessionToken();
    const listActiveDoctors = withCache(
      'findAllActiveDoctors',
      () => this.serverApi.listActiveDoctors(sessionToken),
    );
    return (await listActiveDoctors({}))?.data?.data as DoctorProfile[];
  }

  async getImprovementTimeline(regimenClass: string): Promise<RegimenTimelineImages | null> {
    const userId = this.getActingUser()?.id;
    const sessionToken = this.getSessionToken();
    if (!userId || !sessionToken || !regimenClass) return null;
    const data = await this.fetchImprovementTimeline(userId, sessionToken);
    if (!data) return null;
    const timelineImage = (data.improvementTimeline as Partial<Record<string, ImprovementTimelineEntry>> | undefined)?.[regimenClass]?.timelineImage ?? null;
    const keyTechnologiesImage = (data.keyTechnologies as Partial<Record<string, KeyTechnologiesEntry>> | undefined)?.[regimenClass]?.keyTechnologiesImage ?? null;
    if (!timelineImage && !keyTechnologiesImage) return null;
    return { timelineImage, keyTechnologiesImage };
  }

  private fetchImprovementTimeline(userId: string, sessionToken: string): Promise<ImprovementTimelineAndKeyTechnologiesData | null> {
    if (this.improvementTimelineFetch?.userId === userId) return this.improvementTimelineFetch.promise;
    const promise: Promise<ImprovementTimelineAndKeyTechnologiesData | null> = (async (): Promise<ImprovementTimelineAndKeyTechnologiesData | null> => {
      try {
        const response = await this.serverApi.getImprovementTimeline(sessionToken, userId);
        return response?.data?.data ?? null;
      } catch {
        if (this.improvementTimelineFetch?.promise === promise) this.improvementTimelineFetch = null;
        return null;
      }
    })();
    this.improvementTimelineFetch = { userId, promise };
    return promise;
  }

  async generateHairReport(): Promise<void> {
    const sessionToken = this.getSessionToken();
    if (!sessionToken) return;
    await this.serverApi.generateHairReport(sessionToken);
  }

  // Deliberately not cached: generation runs mid-consultation, so a first report view can predate it.
  async getHairReport(): Promise<AiHairReport | null> {
    const sessionToken = this.getSessionToken();
    const userId = this.getActingUser()?.id;
    if (!sessionToken || !userId) return null;
    const response: GetHairReportResponse = (await this.serverApi.getHairReport(sessionToken, userId))?.data;
    const aiResponse = response?.data as AiResponse | null;
    return (aiResponse?.response as AiHairReport) ?? null;
  }

  // Caching decision (explicit-cache-decision): not cached — status changes when the user uploads a report.
  // Bypass browser HTTP cache: the server sends an ETag and the browser replays it as
  // If-None-Match, getting a 304 with empty body — which makes response.data null.
  async getInvestigationStatus(): Promise<InvestigationStatusResponse | null> {
    const sessionToken = this.getSessionToken();
    if (!sessionToken) return null;
    const response = await this.serverApi.getInvestigationStatus(sessionToken, { headers: { 'Cache-Control': 'no-store' } });
    return response?.data ?? null;
  }
}
